import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

// --- Helper Components ---

// Quest Progress Bar (Slightly updated style) - Scaled Down
const QuestProgressBar = ({ label = "Quest Progress", progress = 0, icon = "🏆" }) => (
  <div className="quest-progress-bar" style={{ marginBottom: '1rem' }}> {/* Reduced margin */}
    <div className="quest-progress-bar-header">
      <span className="quest-progress-bar-icon" style={{ fontSize: '0.9em' }}>{icon}</span>
      <span className="quest-progress-bar-label" style={{ fontSize: '0.85em' }}>{label}: Level {Math.floor(progress / 20) + 1}</span>
    </div>
    <div className="quest-progress-bar-track" style={{ height: '6px' }}>
      <div className="quest-progress-bar-fill" style={{ width: `${progress}%` }}></div>
    </div>
    <span className="quest-progress-bar-percentage" style={{ fontSize: '0.8em' }}>{progress}%</span>
  </div>
);

// ProTip Box (Enhanced style) - Scaled Down
const ProTip = ({ title = "Pro Tip!", children, icon = "💡" }) => (
  <div className="pro-tip-box">
    <h4 className="pro-tip-title">
      <span className="pro-tip-icon">{icon}</span>
      {title}
    </h4>
    <div className="pro-tip-content">{children}</div>
  </div>
);

// Simple SVG Arrow for Flowcharts - Scaled Down
const FlowArrow = () => (
    <svg width="15" height="35" viewBox="0 0 15 35" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 auto', display: 'block'}}>
        <path d="M7.5 0 V30" stroke="#9ca3af" strokeWidth="1.5"/>
        <path d="M3.5 25 L7.5 35 L11.5 25" stroke="#9ca3af" strokeWidth="1.5" fill="none"/>
    </svg>
);

// Simple Box for Flowcharts - Scaled Down
const FlowBox = ({ title, model, description, color = '#eef2ff', borderColor = '#818cf8', icon}) => ( // Added icon prop
    <div className="flow-box" style={{ // Added class for potential mobile styling
        border: `1.5px solid ${borderColor}`,
        backgroundColor: color,
        borderRadius: '6px',
        padding: '0.7rem', // Reduced padding
        margin: '0.4rem auto', // Reduced margin
        textAlign: 'center',
        maxWidth: '220px', // Reduced max width (can be overridden for mobile)
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    }}>
        <strong style={{ display: 'block', marginBottom: '0.2rem', color: borderColor, fontSize: '0.9em' }}>
            {icon && <span style={{ marginRight: '0.3rem', verticalAlign: 'middle' }}>{icon}</span>} {/* Render icon */}
            {title}
        </strong>
        {model && <span style={{ fontSize: '0.75em', color: '#4b5563', display: 'block', marginBottom: '0.3rem' }}>({model})</span>}
        <p style={{ fontSize: '0.8em', color: '#4b5563', margin: 0, lineHeight: 1.5 }}>{description}</p> {/* Adjusted line height */}
    </div>
);


// --- Main Component ---

export default function FoundryDocs() {
  const [activePage, setActivePage] = useState('introduction');
  const [searchTerm, setSearchTerm] = useState(''); // State for search term
  const [expandedSections, setExpandedSections] = useState({
    gettingStarted: true,
    buildingAgents: true,
    deploymentUsage: true,
    scalingAdvanced: true,
    workflows: true,
  });
  const [isMounted, setIsMounted] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // State for mobile sidebar

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Scroll main content to top on page change
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.scrollTo(0, 0);
    }
    // Re-trigger page transition animation
     const contentElement = document.querySelector('.content');
     if (contentElement) {
        contentElement.classList.remove('page-enter-active');
        void contentElement.offsetWidth; // Trigger reflow
        contentElement.classList.add('page-enter-active');
    }
    // Close sidebar on page navigation (mobile)
    setIsSidebarOpen(false);
  }, [activePage]);


  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleResultClick = (pageKey) => {
    setActivePage(pageKey);
    setSearchTerm(''); // Clear search after clicking result
    setIsSidebarOpen(false); // Close sidebar on selection (mobile)
  };

  // Toggle mobile sidebar
  const toggleSidebar = () => {
      setIsSidebarOpen(!isSidebarOpen);
  }

  // --- Page Content Definitions (with searchableText) ---
  // Add a `searchableText` property to each page for easier searching
  // NOTE: CONTENT BELOW IS UNCHANGED EXCEPT FOR THE 'apiUsage' SECTION TO REMOVE 'cid'
  const pageContent = {
    introduction: {
      title: "⚔️ Welcome, Artisan, to Echidna AI Foundry!",
      searchableText: "introduction welcome ai assistants agents custom knowledge purpose forge quest dataset agent model api deploy scale workflow",
      content: (
        <div className="page-content">
          <p>
            You stand at the threshold of innovation! <strong>Echidna AI Foundry</strong> isn't just a platform; it's your digital forge, empowering you to craft bespoke AI assistants – intelligent agents imbued with *your* specific knowledge and designed for *your* unique purposes.
          </p>
          <p>
            Forget generic chatbots. Here, you are the master artisan, shaping AI that understands your documents, supports your customers with precision, analyzes your data, and automates tasks exactly the way you envision.
          </p>
          <h3>Your Grand Quest: From Novice to Foundry Master</h3>
          <p>Embark on this structured journey to unlock the full potential of the Foundry:</p>
          <ol>
            <li><span className="link" onClick={() => setActivePage('datasetCreation')}>📜 Forge Your Knowledge Scrolls</span> (Mastering Dataset Creation & Preprocessing)</li>
            <li><span className="link" onClick={() => setActivePage('agentCreation')}>✨ Awaken Your Golem</span> (Configuring Agent Identity, Skills & Personality)</li>
            <li><span className="link" onClick={() => setActivePage('choosingModel')}>🧠 Choose the Core Crystal</span> (Selecting the Right AI Model - Critical Tactics!)</li>
            <li><span className="link" onClick={() => setActivePage('embeddingAgents')}>🚀 Unleash Your Creation</span> (Deploying & Interacting with Your Agent)</li>
             <li><span className="link" onClick={() => setActivePage('apiUsage')}>🔌 Harness the Power Current</span> (Integrating via the API)</li>
            <li><span className="link" onClick={() => setActivePage('scalingAgents')}>⚙️ Attune the Mechanism</span> (Scaling, Optimizing & Cost Management)</li>
             <li><span className="link" onClick={() => setActivePage('aiWorkflows')}>🗺️ Chart Complex Courses</span> (Designing AI Infrastructure & Workflows)</li>
          </ol>
          <div className="info-box">
            <h4>Ready to Light the Forge?</h4>
            <p>Your adventure begins with gathering knowledge. Let's learn how to create and refine your first Dataset. Proceed to <span className="link" onClick={() => setActivePage('datasetCreation')}>Forge Your Knowledge Scrolls</span>!</p>
          </div>
        </div>
      )
    },
    datasetCreation: {
      title: "📜 Lv 1: Forge Your Knowledge Scrolls",
      searchableText: "dataset data knowledge source create preprocess file upload pdf ppt csv txt docx url scraping webpage preprocess button cost credits time extraction chunking embedding vector indexing vector store database optimize scrolls text ocr structure relevance",
      content: (
        <div className="page-content">
           <QuestProgressBar progress={15} icon="🧱" />
          <p>
            An AI agent without knowledge is like a warrior without a weapon. <strong>Datasets</strong> are the foundational knowledge sources you provide, the very essence your agent will draw upon. Mastering their creation and refinement is the first step to forging a powerful assistant.
          </p>
          <p>Your library of knowledge resides in the <a href="https://www.metabees.org/echidna/efoundry/data" target="_blank" rel="noopener noreferrer" className="link external-link">Foundry Datasets Hub</a>. Head there now to manage your sources!</p>

          <h3>Gathering Raw Materials: Input Methods</h3>
          <p>In the <a href="https://www.metabees.org/echidna/efoundry/data" target="_blank" rel="noopener noreferrer" className="link external-link">Datasets Hub</a>, you can acquire knowledge via:</p>
          <ol>
            <li>
              <strong>Uploading Ancient Texts (File Upload):</strong> Provide your scrolls! The Foundry accepts various formats like <code>.pdf</code>, <code>.ppt</code>/<code>.pptx</code>, <code>.csv</code>, <code>.txt</code>, <code>.docx</code>. The cleaner and more text-focused the document, the better the AI can learn from it.</li>
            <li>
              <strong>Scrying the Digital Ether (URL Scraping):</strong> Point the Foundry to a public webpage (like an FAQ, blog post, or product page). It will diligently attempt to read and understand the text content. <em>(Note: Complex website structures or dynamic content might pose a challenge to the scrying spell!)</em></li>
          </ol>

          <h3>The Alchemist's Fire: Preprocessing Explained</h3>
          <p>Raw knowledge is potent but chaotic. It must be refined through <strong>Preprocessing</strong> before an agent can wield it effectively. Find your newly added source in the <a href="https://www.metabees.org/echidna/efoundry/data" target="_blank" rel="noopener noreferrer" className="link external-link">Datasets Hub</a> and click the crucial ✨ <strong>"Preprocess"</strong> ✨ button.</p>
          <h4>What happens during this transformation?</h4>
          <ul>
              <li><strong style={{color: '#4f46e5'}}>Extraction:</strong> Like deciphering ancient runes, the Foundry pulls text from your diverse sources into a uniform format.</li>
              <li><strong style={{color: '#4f46e5'}}>Chunking:</strong> Huge blocks of text are overwhelming. The Foundry intelligently breaks them into smaller, thematically coherent 'chunks' (paragraphs or logical sections). This helps the AI focus on relevant pieces later.</li>
              <li><strong style={{color: '#4f46e5'}}>Embedding:</strong> This is where the real magic happens! Each chunk is converted into a numerical representation (a 'vector embedding'). Think of it as capturing the *meaning* or *essence* of the text chunk in a way the AI understands. Similar concepts will have similar vector representations.</li>
              <li><strong style={{color: '#4f46e5'}}>Indexing:</strong> These embeddings are stored in a specialized database (a vector store) optimized for lightning-fast similarity searches. When your agent gets a query, it converts the query to an embedding and quickly finds the *most semantically similar* chunks from your dataset.</li>
          </ul>
          <h4>The Toll:</h4>
          <ul>
              <li><strong>Cost:</strong> This intricate process requires resources, costing <strong>100 credits per 5 million characters</strong> processed. You'll always see an estimate before confirming.</li>
              <li><strong>Time:</strong> From a swift <strong>~15 seconds</strong> for short texts to potentially <strong>over an hour</strong> for massive knowledge libraries.</li>
          </ul>

           <ProTip title="Optimize Your Scrolls!">
              <li>**Text is King:** Ensure your PDFs contain selectable text, not just images of text. Use OCR (Optical Character Recognition) on image-based PDFs *before* uploading.</li>
              <li>**Structure Matters:** Well-organized documents with clear headings and paragraphs often lead to better chunking and more accurate retrieval.</li>
              <li>**Relevance Rules:** Only include information you *want* the agent to know. Remove irrelevant sections or outdated documents to keep the knowledge base focused.</li>
           </ProTip>

           <div className="info-box" style={{borderColor: "#10b981", backgroundColor: "#f0fdf4"}}>
                <h4 style={{color: "#047857"}}>Milestone Unlocked: Knowledge Crystalized!</h4>
                <p style={{color: "#059669"}}>Your dataset proudly displays the 'Preprocessed' status! The foundational knowledge for your agent is now ready and waiting.</p>
                <p><strong>Next Quest:</strong> <span className="link" onClick={() => setActivePage('agentCreation')}>✨ Awaken Your Golem!</span></p>
           </div>
        </div>
      )
    },
    agentCreation: {
      title: "✨ Lv 2: Awaken Your Golem (Agent Creation)",
      searchableText: "agent create creation copilot setup identity name purpose skills responsibilities behavior personality communication style model configuration v4o v5-turbo choosing model cost estimate constraints ethics data source dataset hierarchy prompt system prompt collaboration swarm firewall logo",
      content: (
        <div className="page-content">
          <QuestProgressBar progress={40} icon="🤖" />
          <p>
            The knowledge scrolls are prepared! Now, we breathe life into the vessel. Navigate to the agent forge, where you define its essence: <a href="https://www.metabees.org/echidna/copilot" target="_blank" rel="noopener noreferrer" className="link external-link">Create New Agent (Copilot Setup)</a>.
          </p>
          <p>This is where you sculpt its identity, program its skills, imbue it with personality, set its operational boundaries, and finally, connect its AI brain to its knowledge base.</p>

          <h3>The Agent Configuration Grimoire:</h3>

          <h4>1. Core Identity: Name & Purpose</h4>
           <p>What shall this creation be called, and what is its destiny?</p>
          <ul>
            <li><strong>Agent Name *:</strong> A unique and evocative name. <em>(e.g., "Aetherius - Knowledge Keeper", "Support Sentinel MkII")</em></li>
            <li><strong>Agent Purpose *:</strong> Its primary function, its raison d'être. Be precise and action-oriented. (Max 500 chars) <em>(e.g., "To analyze uploaded log files based on the 'Troubleshooting Guide v3' dataset and identify potential error causes.")</em></li>
          </ul>

          <h4>2. Functionality & Behavior: Skills & Demeanor</h4>
           <p>How does it operate? What defines its interactions?</p>
          <ul>
            <li><strong>Key Responsibilities *:</strong> Its detailed operational directives. Use clear, actionable bullet points. Think of these as its core programming. (Max 1000 chars)
              <em>e.g.:<br/>
              - Identify error codes mentioned in the user query.<br/>
              - Cross-reference error codes with the linked 'Troubleshooting Guide'.<br/>
              - Provide the documented solution steps for identified errors.<br/>
              - If an error code is not found, state that clearly and suggest checking the log file format.</em>
            </li>
            <li><strong>Communication Style *:</strong> Its personality and tone. This heavily influences user perception. (Max 500 chars) <em>(e.g., "Calm, analytical, uses technical terms accurately, avoids speculation.", "Friendly guide, breaks down complex steps, uses analogies.")</em></li>
          </ul>

          <h4>3. AI Model Configuration: The Core Crystal ✨</h4>
          <p>Selecting the AI model is paramount – it defines the agent's cognitive abilities and resource needs.</p>
          <ul>
             <li><strong>Select AI Model *:</strong>
                 <ul>
                    <li>⚡️ **Echidna v4o:** The Swift Tactician. Fast, efficient, excels at direct execution based on provided context. Ideal for high-volume Q&A, classification, and tasks where speed and cost-efficiency are prime.</li>
                    <li>🧠 **Echidna v5-Turbo:** The Wise Strategist. Larger, more powerful reasoning engine. Tackles complex instructions, nuance, ambiguity, and multi-step problems. Best for tasks needing deep understanding or creative generation.</li>
                 </ul>
                 <p>This choice has major implications! Study the detailed comparison: <span className="link" onClick={() => setActivePage('choosingModel')}>Choosing Your AI Model [Advanced]</span>.</p>
             </li>
             <li><em>Cost Estimate:</em> Updates below based on model and linked dataset size.</li>
          </ul>

          <h4>4. Constraints & Data Source: Boundaries & Knowledge Link</h4>
          <p>Define what the agent *cannot* or *should not* do, and connect it to the refined knowledge scrolls.</p>
          <ul>
            <li><strong>Constraints & Ethics *:</strong> The operational guardrails. Be explicit about limitations and ethical boundaries. (Max 1000 chars)
              <em>e.g.:<br/>
              - Strictly adhere to information within the provided dataset(s).<br/>
              - Do not offer medical, financial, or legal advice.<br/>
              - Never request Personally Identifiable Information (PII).<br/>
              - Maintain a neutral and objective tone unless style dictates otherwise.</em>
            </li>
            <li><strong>Data Source *:</strong> Grant access to the knowledge!
              <ul>
                <li><strong>Single Dataset:</strong> Link one preprocessed dataset. (Recommended start)</li>
                <li><strong>Dataset Hierarchy:</strong> (Advanced) Combine multiple datasets with a defined parent-child structure for layered knowledge.</li>
              </ul>
               {/* Mock dropdown */}
               <div style={{marginTop: '0.5rem', marginBottom: '1rem'}}>
                 <select disabled style={{padding: '0.4rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.85em'}}><option>-- Select Preprocessed Dataset --</option></select>
               </div>
            </li>
          </ul>

           <ProTip title="The Art of the Prompt!">
               The text you write in "Purpose", "Responsibilities", "Style", and "Constraints" forms the core 'System Prompt' guiding the AI. Clear, unambiguous, and well-structured prompts lead to better, more predictable agent behavior. Iterate and test prompts using the <span className="link" onClick={() => setActivePage('foundrySim')}>Foundry Sim</span>!
           </ProTip>

          {/* Optional Sections */}
          <h4>5. Agent Collaboration: Team Dynamics</h4>
           <ul>
              <li><strong>Standalone Agent:</strong> A lone wolf.</li>
              <li><strong>Connect Agents (Swarm):</strong> Enable sophisticated multi-agent workflows. Delve into <span className="link" onClick={() => setActivePage('agentSwarm')}>Agent Swarms</span>.</li>
           </ul>
          <h4>6. Security & Branding: Defenses & Heraldry</h4>
           <ul>
              <li><strong>Advanced AI Firewall:</strong> Bolster security against misuse (Additional Cost).</li>
              <li><strong>Company/Agent Logo URL:</strong> Give your agent a face in compatible interfaces.</li>
           </ul>

           <div className="info-box" style={{borderColor: "#10b981", backgroundColor: "#f0fdf4"}}>
                <h4 style={{color: "#047857"}}>Milestone Unlocked: Agent Forged & Awakened!</h4>
                <p style={{color: "#059669"}}>The ritual is complete! Hit 'Save'/'Create Agent'. Your custom AI Golem now exists within the Foundry, awaiting deployment.</p>
                <p><strong>Next Quest:</strong> <span className="link" onClick={() => setActivePage('embeddingAgents')}>🚀 Unleash Your Creation!</span></p>
           </div>
        </div>
      )
    },
    choosingModel: {
      title: "🧠 Advanced Tactics: Choosing Your AI Model",
      searchableText: "model select choice v4o v5-turbo comparison swift tactician wise strategist speed efficiency reasoning context adherence cost q&a classification data extraction troubleshooting nuance complexity generation ambiguous queries table test verify assumptions playground sim a/b testing agent swarm",
      content: (
        <div className="page-content">
          <p>
            Selecting the AI Model for your Foundry Agent is a critical strategic decision. It determines the fundamental cognitive capabilities, processing speed, operational cost, and ultimately, the suitability of your agent for its intended purpose. This isn't just about picking "better" or "worse," but about choosing the *right tool for the job*.
          </p>
          <p>Consult the detailed specifications and performance benchmarks on the <a href="https://www.metabees.org/echidna/efoundry/models" target="_blank" rel="noopener noreferrer" className="link external-link">Foundry Models Comparison</a> page.</p>

          <section className="model-profile v4o-profile">
            <h3 className="model-title"><span className="model-icon">⚡️</span>Echidna v4o: The Swift Tactician</h3>
            <p className="model-summary">
              v4o is engineered for <strong>speed, efficiency, and precise execution</strong> based on provided context. It's a smaller, highly optimized model ideal for tasks requiring quick responses and direct adherence to instructions and data.
            </p>
            <h4>Key Characteristics:</h4>
            <ul className="model-characteristics">
              <li><strong>Processing Speed:</strong> Very High</li>
              <li><strong>Reasoning Depth:</strong> Moderate (Excels with explicit context)</li>
              <li><strong>Context Adherence:</strong> Very High (Less prone to hallucination if context is clear)</li>
              <li><strong>Cost-Efficiency:</strong> Excellent, especially for input tokens. Often the most economical choice for high-volume, clearly defined tasks.</li>
              <li><strong>Best Suited For:</strong>
                  <ul>
                      <li>High-throughput Q&A systems directly referencing dataset content.</li>
                      <li>Rapid input classification and categorization.</li>
                      <li>Efficient data extraction based on defined patterns.</li>
                      <li>Task-oriented bots where speed and literal interpretation are paramount.</li>
                      <li>Cost-sensitive applications handling large volumes of straightforward interactions.</li>
                  </ul>
              </li>
            </ul>
             <h4>Analogy:</h4>
             <p>Think of v4o as a highly skilled surgical assistant: incredibly fast and precise when following the surgeon's (your prompt and data) explicit instructions, but not designed for independent diagnosis or complex strategic planning.</p>
          </section>

          <section className="model-profile v5-profile">
              <h3 className="model-title"><span className="model-icon">🧠</span>Echidna v5-Turbo: The Wise Strategist</h3>
              <p className="model-summary">
                  v5-Turbo represents a larger, more powerful cognitive engine focused on <strong>deep reasoning, understanding nuance, handling complexity, and synthesizing information</strong>. It excels where the answer isn't explicitly stated but must be inferred or constructed.
              </p>
              <h4>Key Characteristics:</h4>
              <ul className="model-characteristics">
                  <li><strong>Processing Speed:</strong> Moderate to High (Slower than v4o due to size/depth)</li>
                  <li><strong>Reasoning Depth:</strong> Very High</li>
                  <li><strong>Context Adherence:</strong> High (but more capable of inference beyond literal text)</li>
                  <li><strong>Cost-Efficiency:</strong> Moderate. Higher per-token costs reflect greater capabilities. Best value when its advanced reasoning is necessary.</li>
                  <li><strong>Best Suited For:</strong>
                      <ul>
                          <li>Complex troubleshooting involving multiple logical steps.</li>
                          <li>Analyzing and synthesizing information from various data points within the context.</li>
                          <li>Generating nuanced, creative, or highly detailed text (reports, explanations).</li>
                          <li>Understanding and responding effectively to ambiguous or poorly phrased user queries.</li>
                          <li>Applications requiring deeper conversational understanding and adaptability.</li>
                      </ul>
                  </li>
              </ul>
               <h4>Analogy:</h4>
               <p>Think of v5-Turbo as a seasoned detective: takes time to examine clues (context), connect disparate pieces of information, understand underlying motives (nuance), and construct a comprehensive theory (response) that goes beyond just reporting the facts found at the scene.</p>
          </section>

          <h3 style={{ marginTop: '2.5rem' }}>Strategic Selection Guide:</h3> {/* Reduced margin */}
          <div style={{overflowX: 'auto', paddingBottom: '0.5rem'}}> {/* Allow table scroll on mobile */}
            <table className="comparison-table">
                <thead>
                    <tr>
                        <th>Consideration / Task</th>
                        <th>⚡️ Echidna v4o (Swift Tactician)</th>
                        <th>🧠 Echidna v5-Turbo (Wise Strategist)</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td><strong>Primary Goal: Speed & Volume</strong></td>
                        <td className="preferred">Highly Recommended</td>
                        <td>Capable, but potentially overkill/slower</td>
                    </tr>
                    <tr>
                        <td><strong>Primary Goal: Deep Reasoning/Complexity</strong></td>
                        <td>May struggle with nuance/inference</td>
                        <td className="preferred">Highly Recommended</td>
                    </tr>
                     <tr>
                        <td><strong>Answering Direct Factual Qs from Data</strong></td>
                        <td className="preferred">Excellent & Efficient</td>
                        <td>Excellent, but potentially less cost-effective</td>
                    </tr>
                    <tr>
                        <td><strong>Handling Ambiguous Queries</strong></td>
                        <td>May need very precise prompting</td>
                        <td className="preferred">More robust understanding</td>
                    </tr>
                    <tr>
                        <td><strong>Generating Creative/Detailed Text</strong></td>
                        <td>Can follow instructions, but less nuanced</td>
                        <td className="preferred">Stronger generative capabilities</td>
                    </tr>
                     <tr>
                        <td><strong>Multi-Step Problem Solving</strong></td>
                        <td>Possible with very structured prompts</td>
                        <td className="preferred">Designed for complex reasoning</td>
                    </tr>
                     <tr>
                        <td><strong>Cost Sensitivity (per Interaction)</strong></td>
                        <td className="preferred">Generally Lower Cost</td>
                        <td>Higher Cost (justified by capability)</td>
                    </tr>
                </tbody>
            </table>
           </div>

           <ProTip title="Test & Verify Your Assumptions!" icon="🔬">
               While these guidelines are robust, the *best* model truly depends on your specific dataset, prompt engineering, and user interaction patterns.
               <ul>
                   <li>Use the <span className="link" onClick={() => setActivePage('foundrySim')}>Foundry Sim</span> for direct side-by-side comparisons with your data.</li>
                   <li>Employ <span className="link" onClick={() => setActivePage('abTesting')}>A/B Testing</span> with real or simulated traffic for definitive performance data.</li>
                   <li>Consider <span className="link" onClick={() => setActivePage('agentSwarm')}>Agent Swarms</span> to leverage *both* models for different sub-tasks within a single workflow.</li>
               </ul>
           </ProTip>

           <div className="info-box">
                <h4>Empower Your Agent with the Right Mind!</h4>
                <p>This choice fundamentally shapes your agent. Choose deliberately during <span className="link" onClick={() => setActivePage('agentCreation')}>Agent Creation</span>, informed by the task requirements and your testing.</p>
           </div>
        </div>
      )
    },
    embeddingAgents: {
      title: "🚀 Lv 3: Unleash Your Creation! (Using Agents)",
      searchableText: "deploy deployment use usage interact agent standalone page url website embed script html api endpoint post request key integrate custom backend test secret monitor rate limit cost",
      content: (
        <div className="page-content">
          <QuestProgressBar progress={70} icon="🚀" />
          <p>Your agent pulses with potential! Now, let's open the gates and allow interaction. Echidna AI Foundry provides several methods to deploy your agent, catering to different needs and technical skills.</p>
          <p>Manage and obtain deployment artifacts here: <a href="https://www.metabees.org/echidna/efoundry/deployments" target="_blank" rel="noopener noreferrer" className="link external-link">Foundry Deployments</a>.</p>

          <h3>Deployment Methods: Choose Your Gateway</h3>

          <div className="deployment-option">
              <h4>1. Instant Portal (Standalone Page) 🪄</h4>
              <p>The quickest way to test and share. Foundry hosts a dedicated webpage for your agent.</p>
              <ul>
                  <li><strong>How:</strong> Grab the unique URL from the <a href="https://www.metabees.org/echidna/efoundry/deployments" target="_blank" rel="noopener noreferrer" className="link external-link">Deployments</a> page.</li>
                  <li><strong>Use Cases:</strong> Internal testing, quick demos, sharing with non-technical users, basic interaction point.</li>
                  <li><strong>Effort Level:</strong> Minimal ⭐</li>
              </ul>
          </div>

           <div className="deployment-option">
              <h4>2. Website Familiar (Embed Script) 💬</h4>
              <p>Integrate a chat widget directly onto your own website with a simple copy-paste.</p>
              <ul>
                   <li><strong>How:</strong> Copy the provided HTML <code>&lt;script&gt;</code> tag from <a href="https://www.metabees.org/echidna/efoundry/deployments" target="_blank" rel="noopener noreferrer" className="link external-link">Deployments</a> and add it to your website's HTML source code (usually before the closing <code>&lt;/body&gt;</code> tag).</li>
                  <li><strong>Use Cases:</strong> Adding live support chat, embedding an internal knowledge bot, providing interactive help on specific pages.</li>
                  <li><strong>Effort Level:</strong> Low ⭐⭐ (Requires access to website code)</li>
              </ul>
          </div>

           <div className="deployment-option">
              <h4>3. Direct Conduit (API Endpoint) 🔗</h4>
              <p>For maximum flexibility and custom integration. Allows your applications to communicate programmatically with the agent.</p>
              <ul>
                  <li><strong>How:</strong> Use the unique API Endpoint URL and your Agent's Secret Key (found in <a href="https://www.metabees.org/echidna/efoundry/deployments" target="_blank" rel="noopener noreferrer" className="link external-link">Deployments</a> or settings) to make HTTP POST requests from your backend code.</li>
                  <li><strong>Use Cases:</strong> Building custom user interfaces, integrating agent logic into backend processes, automated reporting, complex multi-system workflows.</li>
                  <li><strong>Effort Level:</strong> High ⭐⭐⭐⭐ (Requires programming expertise)</li>
                  <li>➡️ **Deep Dive:** Consult the <span className="link" onClick={() => setActivePage('apiUsage')}>Using the Agent API</span> guide for detailed instructions and code examples.</li>
              </ul>
          </div>


           <ProTip title="Deployment Best Practices" icon="🛡️">
               <li>**Test Thoroughly:** Use the Standalone Page or a staging environment for Embed/API testing before exposing the agent to end-users.</li>
               <li>**Secure Your Secrets:** Never embed API Secret Keys directly in client-side code. Use a secure backend proxy.</li>
                <li>**Monitor Usage:** Keep an eye on interaction logs and analytics (if available) to understand how your agent is being used and identify potential issues.</li>
           </ProTip>

           <div className="info-box" style={{borderColor: "#f59e0b", backgroundColor: "#fffbeb"}}>
                <h4 style={{color: "#b45309"}}>⚠️ Guard the Gates & Manage Resources!</h4>
                <p style={{color: "#92400e"}}>Remember: Protect your API keys! Critically, configure <span className="link" onClick={() => setActivePage('scalingAgents')}>Rate Limits</span> via the <a href="https://www.metabees.org/echidna/efoundry/deployments" target="_blank" rel="noopener noreferrer" className="link external-link">Deployments</a> page to prevent abuse and control operational costs.</p>
           </div>

           <div className="info-box" style={{borderColor: "#10b981", backgroundColor: "#f0fdf4"}}>
                <h4 style={{color: "#047857"}}>Milestone Unlocked: Agent Unleashed!</h4>
                <p style={{color: "#059669"}}>Your agent is now accessible via your chosen method! Observe its performance in the wild.</p>
                <p><strong>Enhance Your Creation:</strong> Explore <span className="link" onClick={() => setActivePage('scalingAgents')}>Scaling & Optimization</span>, or advanced paths like <span className="link" onClick={() => setActivePage('agentSwarm')}>Agent Swarms</span> and <span className="link" onClick={() => setActivePage('apiUsage')}>API Integration</span>.</p>
           </div>
        </div>
      )
    },
    // --- Start of MODIFIED apiUsage section (cid removed) ---
    apiUsage: {
      title: "🔌 Lv 3: Connect Your Creation (API Integration)",
      searchableText: "api integration connect usage implementation code example axios fetch request response error handling conversation history context management token usage rate limiting",
      content: (
        <div className="page-content">
          <div className="code-section">
            <h3>Direct API Integration Example</h3>
            <div className="code-container">
              <pre>
                <code className="language-javascript">
{`// Agent API Integration Example
import axios from 'axios';

async function askAgent(targetAgentId, userMessage, conversationHistory = []) {
  // Configuration Check
  if (!targetAgentId || !agentSecret) {
    throw new Error("Agent configuration missing on server.");
  }

  const endpointUrl = \`\${foundryApiBaseUrl}/api/v1/agents/\${targetAgentId}/chat\`;

  try {
    const response = await axios.post(endpointUrl, {
      message: userMessage,
      secret: agentSecret,
      previousMessages: conversationHistory
    }, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 15000
    });

    return response.data;

  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.error || 
        \`Agent API request failed (\${error.response.status})\`);
    }
    throw new Error(\`Failed to communicate with agent: \${error.message}\`);
  }
}

// Example Usage
const conversationStore = {};

async function handleChatMessage(userId, currentMessage) {
  if (!conversationStore[userId]) {
    conversationStore[userId] = [];
  }

  try {
    const historyForApi = [...conversationStore[userId]];
    const agentResponse = await askAgent(
      'your-agent-id',
      currentMessage,
      historyForApi
    );

    // Update conversation history
    conversationStore[userId].push(
      { sender: 'user', message: currentMessage },
      { sender: 'agent', message: agentResponse.message }
    );

    // Optional: Limit history size
    const MAX_HISTORY = 10;
    if (conversationStore[userId].length > MAX_HISTORY * 2) {
      conversationStore[userId] = conversationStore[userId]
        .slice(-MAX_HISTORY * 2);
    }

    return agentResponse.message;
  } catch (error) {
    console.error('Chat error:', error);
    throw new Error("Sorry, I couldn't process that request.");
  }
}`}
              </code>
            </pre>
          </div>
        </div>

        <ProTip title="Managing Conversation State" icon="🧠">
          <p>Your application is now fully responsible for maintaining conversational context.</p>
          <ul>
            <li>Store conversation history per user session</li>
            <li>Pass relevant history in the previousMessages array</li>
            <li>Be mindful of context window limits</li>
          </ul>
        </ProTip>

        {/* Rest of the content */}
      </div>
    )
  },
  // --- End of MODIFIED apiUsage section ---
  scalingAgents: {
    title: "⚙️ Lv 4: Attune the Mechanism (Scaling & Optimization)",
    searchableText: "scale scaling optimize optimization performance quality rate limiting cost control token usage model choice dataset quality agent configuration refine tune retrieval chunking embedding experimentation a/b testing feedback logs",
    content: (
      <div className="page-content">
         <QuestProgressBar progress={85} icon="⚙️" />
        <p>Your agent is deployed and interacting! But the journey of a master artisan involves continuous refinement. Let's explore how to manage costs, ensure smooth performance under load, and iteratively improve your agent's quality.</p>

        <h3>Guardian Rituals: Rate Limiting & Cost Control</h3>
        <p>Uncontrolled usage can lead to unexpected costs and performance degradation. Implement safeguards!</p>
        <ul>
          <li><strong>Rate Limiting:</strong> This is non-negotiable for public-facing agents (Embed/API). Set limits (e.g., requests per minute per user/API key) in the agent's settings on the <a href="https://www.metabees.org/echidna/efoundry/deployments" target="_blank" rel="noopener noreferrer" className="link external-link">Deployments Page</a>.
              <ul><li>*Why?* Prevents abuse, controls costs, ensures fair resource allocation. Start conservatively!</li></ul>
          </li>
           <li><strong>Monitor Token Usage:</strong> Pay attention to the `tokenUsage` returned by the API. Are certain query types consuming excessive tokens? This might indicate inefficient prompting or a need for dataset refinement.</li>
           <li><strong>Model Choice Review:</strong> Is the cost of 🧠 v5-Turbo justified for *all* interactions, or could some tasks be handled more cheaply by ⚡️ v4o? (See <span className="link" onClick={() => setActivePage('agentSwarm')}>Agent Swarms</span>). Check the <a href="https://www.metabees.org/echidna/efoundry/models" target="_blank" rel="noopener noreferrer" className="link external-link">Models Comparison</a> for cost details.</li>
        </ul>

        <h3>Enhancing Performance & Quality: The Path of Iteration</h3>
        <ul>
          <li><strong style={{color:'#1e40af'}}>Refine the Source (Dataset Quality):</strong> This often yields the biggest improvements. Periodically review datasets on the <a href="https://www.metabees.org/echidna/efoundry/data" target="_blank" rel="noopener noreferrer" className="link external-link">Datasets Hub</a>. Remove ambiguities, correct errors, add missing information, improve structure.</li>
          <li><strong style={{color:'#1e40af'}}>Tune the Core (Agent Configuration):</strong> Small changes to Purpose, Responsibilities, Style, or Constraints in the <a href="https://www.metabees.org/echidna/copilot" target="_blank" rel="noopener noreferrer" className="link external-link">Agent Setup</a> can dramatically alter behavior. Use the <span className="link" onClick={() => setActivePage('foundrySim')}>Foundry Sim</span> for rapid prompt testing.</li>
           <li><strong style={{color:'#1e40af'}}>Optimize Retrieval (Advanced):</strong> Explore options related to chunking strategy or embedding models if available – sometimes tuning how knowledge is stored and retrieved can boost relevance.</li>
          <li><strong style={{color:'#1e40af'}}>Embrace Experimentation (<span className="link" onClick={() => setActivePage('abTesting')}>A/B Testing</span>):</strong> Don't rely on intuition alone. Use the <a href="https://www.metabees.org/echidna/efoundry/playground" target="_blank" rel="noopener noreferrer" className="link external-link">Playground</a> to systematically test variations and validate improvements with data.</li>
        </ul>

         <ProTip title="Feedback is Gold!" icon="👂">
            If possible, implement mechanisms to gather user feedback directly within your application (e.g., thumbs up/down on responses, short surveys). Analyze logs for common failure points or confusing interactions. This real-world data is invaluable for targeted optimization.
         </ProTip>

         <div className="info-box">
          <h4>The Artisan's Pursuit</h4>
          <p>Agent mastery is an ongoing process of monitoring, analyzing, refining, and testing. Embrace the iterative cycle to continuously enhance your creations.</p>
        </div>

         <div className="info-box" style={{borderColor: "#10b981", backgroundColor: "#f0fdf4"}}>
              <h4 style={{color: "#047857"}}>Milestone Unlocked: Master Artificer!</h4>
              <p style={{color: "#059669"}}>You now understand the principles of managing, scaling, and iteratively improving your Foundry Agents!</p>
              <p><strong>Explore the Frontiers:</strong> <span className="link" onClick={() => setActivePage('agentSwarm')}>Agent Swarms</span>, <span className="link" onClick={() => setActivePage('abTesting')}>A/B Testing</span>, <span className="link" onClick={() => setActivePage('aiWorkflows')}>AI Workflows</span>.</p>
         </div>
      </div>
    )
  },
  agentSwarm: {
    title: "🌟 Advanced Strategy: Command an Agent Swarm!",
    searchableText: "agent swarm swarms advanced multiple agents collaboration delegate specialization orchestration router coordinator flowchart v4o v5-turbo cost efficiency accuracy maintainability scalability workflow automation connect agents delegation logic communication protocols sim",
    content: (
      <div className="page-content">
        <p>Why task a single agent with mastering *everything* when you can orchestrate a symphony of specialized experts? <strong>Agent Swarms</strong> allow you to connect multiple Foundry Assistants, enabling them to collaborate, delegate, and conquer complex challenges far beyond the reach of any lone agent.</p>

        <h3>The Philosophy: Distributed Cognition</h3>
          <h3>The Philosophy: Distributed Cognition</h3>
          <p>Instead of one monolithic AI attempting to juggle diverse knowledge domains and tasks (often inefficiently), the Swarm approach advocates for:</p>
          <ul>
              <li><strong>Specialization:</strong> Create individual agents hyper-focused on specific tasks or knowledge areas (e.g., Billing vs. Tech Support vs. Product Features).</li>
              <li><strong>Orchestration:</strong> Designate one or more agents to act as routers or coordinators, intelligently directing user queries or sub-tasks to the most appropriate specialist agent.</li>
              <li><strong>Collaboration:</strong> Enable agents to pass information, context, and control between each other to achieve a collective goal.</li>
          </ul>

          <h3>Visualizing the Flow: Multi-Model Support Swarm</h3>
          <p>Consider our enhanced customer support example, leveraging different models for optimal cost and performance:</p>
          {/* Flowchart using helper components */}
          <div className="swarm-flowchart">
              <FlowBox title="User Query Arrives" description="Initial contact point" color="#f3f4f6" borderColor="#6b7280"/>
              <FlowArrow/>
              <FlowBox title="Agent 1: Triage Bot" model="⚡️ v4o (Fast/Cheap)" description="Identifies query type (Billing, Tech, Sales?)" color="#e0e7ff" borderColor="#4f46e5"/>
              {/* Branching Logic Visualization (Conceptual) */}
              <div className="flowchart-branching"> {/* Added class for mobile styling */}
                  {/* Path 1: Billing */}
                  <div className="flowchart-path"> {/* Added class */}
                      <span className="flowchart-branch-label" style={{ color: '#4f46e5' }}>→ If Billing Query</span>
                       <FlowArrow/>
                       <FlowBox title="Agent 2: Billing Expert" model="⚡️ v4o (Accurate/Fast)" description="Answers billing questions using FAQ dataset." color="#dbeafe" borderColor="#2563eb"/>
                       <FlowArrow/>
                  </div>
                   {/* Path 2: Tech Support */}
                  <div className="flowchart-path"> {/* Added class */}
                      <span className="flowchart-branch-label" style={{ color: '#b45309'}}>→ If Tech Query</span>
                       <FlowArrow/>
                       <FlowBox title="Agent 3: Tech Guru" model="🧠 v5-Turbo (Reasoning)" description="Troubleshoots complex issues using manuals dataset." color="#fef3c7" borderColor="#d97706"/>
                       <FlowArrow/>
                  </div>
                   {/* Path 3: Sales */}
                   <div className="flowchart-path"> {/* Added class */}
                      <span className="flowchart-branch-label" style={{ color: '#15803d'}}>→ If Sales Query</span>
                       <FlowArrow/>
                       <FlowBox title="Agent 4: Sales Closer" model="⚡️ v4o (Quick/Persuasive?)" description="Provides product features and benefits." color="#dcfce7" borderColor="#16a34a"/>
                       <FlowArrow/>
                   </div>
              </div>
              <FlowBox title="Coordinated Response to User" description="Answer provided by the relevant specialist agent" color="#f3f4f6" borderColor="#6b7280"/>
          </div>

          <h4 style={{marginTop: '1.8rem'}}>Interpreting the Flow:</h4> {/* Reduced margin */}
          <ol>
              <li>The initial query hits the lightweight **Triage Bot (v4o)**. Its sole, cheap task is classification.</li>
              <li>Based on the classification, it internally routes the query (along with necessary context like conversation history) to the designated specialist.</li>
              <li>The **Billing Expert (v4o)** or **Sales Closer (v4o)** handles straightforward tasks efficiently using their focused datasets.</li>
              <li>Only when complex reasoning is needed (Tech Support) is the more powerful and expensive **Tech Guru (v5-Turbo)** invoked.</li>
              <li>The specialist agent generates the response, which is ultimately delivered to the user.</li>
          </ol>


          <h3>Strategic Advantages of Swarms:</h3>
          <ul>
            <li>💰 **Cost Efficiency:** Dramatically reduce costs by using cheaper models (v4o) for the bulk of simple interactions and reserving expensive models (v5-Turbo) *only* for tasks that demand their power.</li>
            <li>🎯 **Enhanced Accuracy:** Specialists trained on focused datasets often outperform generalist agents within their domain.</li>
            <li>🧩 **Maintainability:** Update the billing knowledge by modifying *only* the Billing Expert, simplifying maintenance.</li>
            <li>📈 **Scalability:** Add new specialists (e.g., "Returns Agent") without overhauling the entire system. Handles greater complexity gracefully.</li>
            <li>⚙️ **Workflow Automation:** Chain agents together: one extracts data, another analyzes it, a third summarizes the findings.</li>
          </ul>


          <h3>Enabling Swarm Behavior:</h3>
          <p>Within an agent's configuration (<a href="https://www.metabees.org/echidna/copilot" target="_blank" rel="noopener noreferrer" className="link external-link">Agent Setup</a>):</p>
          <ol>
            <li>Select the **"Connect Agents (Swarm)"** option under the Collaboration/Advanced settings.</li>
            <li>Specify *which other agents* this agent is permitted to communicate with or delegate tasks to.</li>
            <li>Crucially, define the **delegation logic** within the agent's prompts (Responsibilities/Constraints). E.g., "If the user asks about pricing, invoke the 'Pricing Bot' agent with the user's exact query."</li>
          </ol>


          <ProTip title="Design Communication Protocols!" icon="📡">
              Effective swarms require clear rules of engagement. Define:
              <ul>
                  <li>*When* should an agent delegate? (Based on keywords, query complexity, confidence score?)</li>
                  <li>*What* information should be passed? (Original query? History? Summarized context?)</li>
                  <li>*How* should the receiving agent respond? (Directly to the user? Back to the calling agent?)</li>
              </ul>
             Start simple (e.g., a basic router) and add complexity incrementally. Test handoffs rigorously in the <span className="link" onClick={() => setActivePage('foundrySim')}>Foundry Sim</span>!
          </ProTip>

          <div className="info-box">
            <h4>Beyond Solo Agents: Orchestrate Intelligence!</h4>
            <p>Agent Swarms unlock a new level of sophistication, allowing you to build robust, efficient, and highly specialized AI systems tailored precisely to complex operational needs.</p>
          </div>
        </div>
      )
    },
    abTesting: {
      title: "🧪 Advanced Technique: Scientific Agent Refinement (A/B Testing)",
      searchableText: "a/b testing experiment scientific refinement playground compare control variant model prompt style dataset constraint variable traffic simulation split kpi success satisfaction accuracy cost response time statistical significance",
      content: (
        <div className="page-content">
          <p>Intuition is good, but data is better! How do you *quantify* the impact of changing your agent's model, prompt, or dataset? Through rigorous, controlled experimentation: <strong>A/B Testing</strong>.</p>
          <p>The <a href="https://www.metabees.org/echidna/efoundry/playground" target="_blank" rel="noopener noreferrer" className="link external-link">Foundry Playground</a> is your laboratory for designing and comparing these experiments.</p>

          <h3>The Alchemist's Method: The A/B Test</h3>
          <p>The principle is straightforward: create two versions of your agent (A - Control, B - Variant) that differ in precisely *one* element. Expose them to similar traffic (real or simulated) and measure which performs better against your defined goals.</p>
          <h4>Testable Variables (Change Only One!):</h4>
          <ul>
            <li><strong>AI Model:</strong> Does 🧠 v5-Turbo truly outperform ⚡️ v4o for *this specific task* in terms of accuracy or user satisfaction, justifying its cost?</li>
            <li><strong>Prompt Wording:</strong> Does changing a key phrase in the "Responsibilities" lead to better task adherence?</li>
            <li><strong>Communication Style:</strong> Does a more empathetic tone reduce user frustration compared to a purely factual one?</li>
            <li><strong>Dataset Version:</strong> Does incorporating the latest FAQ document improve answer correctness?</li>
            <li><strong>Constraint Strictness:</strong> Does slightly loosening a constraint allow the agent to be more helpful without becoming unsafe?</li>
          </ul>

          <h3>Conducting the Experiment (Conceptual Workflow):</h3>
          <ol>
            <li><strong>Clone Your Champion:</strong> Duplicate your current best-performing agent (A) via the <a href="https://www.metabees.org/echidna/copilot" target="_blank" rel="noopener noreferrer" className="link external-link">Agent Setup</a>.</li>
            <li><strong>Introduce the Mutation:</strong> Modify *only the single variable* you want to test in the cloned agent (B).</li>
            <li><strong>Trial by Fire (Traffic/Simulation):</strong>
                <ul>
                    <li>**Playground/Sim:** Use the <a href="https://www.metabees.org/echidna/efoundry/playground" target="_blank" rel="noopener noreferrer" className="link external-link">Playground</a> or <span className="link" onClick={() => setActivePage('foundrySim')}>Sim</span> for direct side-by-side comparisons on identical inputs. Excellent for initial validation.</li>
                    <li>**Live Traffic Splitting:** For real-world data, configure your deployment (API gateway, application logic, or potentially advanced Foundry features) to route a portion (e.g., 50/50) of user traffic to Agent A and Agent B.</li>
                </ul>
            </li>
            <li><strong>Gather Evidence (Measure KPIs):</strong> Track key metrics diligently for both A and B over a meaningful period:
                <ul>
                    <li>✅ Task Success Rate</li>
                    <li>⭐ User Satisfaction Scores</li>
                    <li>🎯 Answer Accuracy / Relevance (may require human review)</li>
                    <li>💰 Cost Per Interaction / Token Usage</li>
                    <li>⏱️ Average Response Time</li>
                    <li>📉 Escalation Rate (if applicable)</li>
                </ul>
            </li>
            <li><strong>Judgment Day (Analyze):</strong> Did variant B show a statistically significant improvement in your primary metric(s) without negatively impacting others unacceptably? If yes, promote B to be the new champion! If not, the control (A) remains, or you design a new experiment.</li>
          </ol>

          <ProTip title="Statistical Significance Matters!" icon="📈">
             Don't declare a winner based on small differences or short tests. Ensure you have enough data points and use basic statistical methods (or tools) to confirm if the observed difference is real or just random chance. The sample size needed depends on the baseline performance and the expected effect size.
          </ProTip>

          <div className="info-box">
            <h4>Experiment, Measure, Improve!</h4>
            <p>A/B testing transforms agent optimization from guesswork into a data-driven science. Leverage the <a href="https://www.metabees.org/echidna/efoundry/playground" target="_blank" rel="noopener noreferrer" className="link external-link">Foundry Playground</a> and systematic testing to unlock peak performance.</p>
          </div>
        </div>
      )
    },
    foundrySim: {
      title: "🎮 Bonus Level: The Foundry Simulation Chamber!",
      searchableText: "simulation sim test chamber scenario performance validation edge case benchmark comparison debug debugging cost forecast parameters persona message count",
      content: (
        <div className="page-content">
          <p>Step into the <strong>Foundry Simulation Chamber</strong> – your consequence-free holodeck for rigorously testing agent behavior, comparing configurations, and stress-testing scenarios before unleashing them upon actual users!</p>
           <p>Access the chamber and run your tests here: <a href="https://www.metabees.org/echidna/efoundry/simulations" target="_blank" rel="noopener noreferrer" className="link external-link">Foundry Simulations</a>.</p>

          <h3>Running Simulated Conversations:</h3>
          <p>The <a href="https://www.metabees.org/echidna/efoundry/simulations" target="_blank" rel="noopener noreferrer" className="link external-link">Simulations</a> tool allows you to define and execute controlled interaction scenarios:</p>
          <div className="simulation-setup-box">
              <div className="sim-param">
                  <label htmlFor="sim-agent-select">Select Agent:</label>
                  <select id="sim-agent-select" disabled><option>Select an agent...</option></select>
                  <span>Choose the agent version to test.</span>
              </div>
               <div className="sim-param">
                  <label htmlFor="sim-user-type">Customer / User Type Persona:</label>
                  <input id="sim-user-type" type="text" placeholder="e.g., Confused beginner asking basic questions" disabled/>
                  <span>Define the user's context, attitude, or goal.</span>
              </div>
              <div className="sim-param">
                  <label htmlFor="sim-message-count">Number of Messages (Turns):</label>
                  <input id="sim-message-count" type="number" defaultValue="5" disabled />
                   <span>Set the length of the simulated dialogue.</span>
              </div>
              {/* Potential future additions: Initial Prompt, Specific Dataset Subset */}
              <button disabled className="sim-run-button">Run Simulation</button>
          </div>


          <h3>Why Enter the Simulation Chamber?</h3>
          <ul className="benefits-list">
            <li><span className="benefit-icon">🧪</span> <strong>Performance Testing:</strong> Evaluate how the agent handles different user personas (frustrated, technical, vague) and conversation lengths. Does it stay on track? Does performance degrade?</li>
            <li><span className="benefit-icon">💡</span> <strong>Scenario Validation:</strong> Test specific edge cases, common failure points, or critical workflows (e.g., simulating a user trying to bypass constraints).</li>
            <li><span className="benefit-icon">📊</span> <strong>Benchmarking & Comparison:</strong> Run identical simulations against Agent A vs. Agent B for objective <span className="link" onClick={() => setActivePage('abTesting')}>A/B testing</span> data before live deployment. Compare v4o vs. v5-Turbo under controlled conditions.</li>
            <li><span className="benefit-icon">🔧</span> <strong>Targeted Debugging:</strong> Reproduce problematic real-world interactions in the sim to analyze the agent's internal state (if available) or response generation step-by-step.</li>
            <li><span className="benefit-icon">💰</span> <strong>Cost Forecasting:</strong> Get estimates of token consumption for typical conversation patterns defined by your simulation parameters, helping budget appropriately.</li>
          </ul>

          <ProTip title="Design Meaningful Simulations!" icon="🎯">
             Generic simulations yield generic results. Be specific!
             <ul>
                 <li>Define personas clearly: "User asking about return policy after 30 days," "Developer trying to integrate API," "User providing incomplete information."</li>
                 <li>Test sequences: Simulate a common multi-step task.</li>
                 <li>Vary message counts: How does context handling perform over longer conversations?</li>
             </ul>
          </ProTip>

          <div className="info-box">
            <h4>Your Agent's Sparring Partner</h4>
            <p>The <a href="https://www.metabees.org/echidna/efoundry/simulations" target="_blank" rel="noopener noreferrer" className="link external-link">Foundry Simulations</a> tool is indispensable for building robust, reliable, and well-understood agents. Make it a core part of your development and QA process!</p>
          </div>
        </div>
      )
    },
    aiWorkflows: {
        title: "🗺️ Grand Master Cartography: Designing AI Infrastructure & Workflows",
        searchableText: "workflow workflows infrastructure design pattern integration orchestration escalation customer support document analysis summarization content creation pipeline triage diagnostic faq bot api crm human-in-the-loop extractor sentiment summarizer drafting refinement data stores triggers parallel branching loops conditional logic multi-agent swarm sales marketing operations finance hr research development productivity examples basic intermediate advanced complex n8n",
        content: (
            <div className="page-content">
                <p>You've mastered individual agents; now, become the Grand Master, orchestrating intricate AI symphonies. Foundry agents achieve their zenith when woven into sophisticated workflows, automating complex processes, augmenting human capabilities, and interacting seamlessly with your existing digital ecosystem.</p>
                <p>This section presents a comprehensive library of <strong>30 workflow patterns</strong>, escalating from foundational concepts to highly advanced, multi-system integrations. Visualize how agents collaborate, leverage different models, trigger external actions, and enable intelligent automation across diverse business functions.</p>

                {/* --- Basic Workflow Examples (1-10) --- */}
                <h3 style={{marginTop:'2rem', borderTop:'2px solid #e5e7eb', paddingTop:'2rem'}}>I. Basic Workflows (Single Agent / Linear Flow)</h3>

                <div className="workflow-example">
                    <h4>1. Simple FAQ Bot</h4>
                    <p><strong>Goal:</strong> Answer common questions based on a single dataset.</p>
                    <div className="workflow-diagram basic-linear">
                        <FlowBox title="User Query" description="(Chat Widget)" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="FAQ Agent" model="⚡️ v4o" description="Finds answer in 'Help Docs' dataset." color="#e0e7ff" borderColor="#4f46e5"/>
                        <FlowArrow/>
                        <FlowBox title="Agent Response" description="Provides answer to user." color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                </div>

                <div className="workflow-example">
                    <h4>2. Basic Data Entry Assistant</h4>
                    <p><strong>Goal:</strong> Extract specific fields from unstructured text input.</p>
                    <div className="workflow-diagram basic-linear">
                        <FlowBox title="User Pastes Text" description="(e.g., Email Body)" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Extraction Agent" model="⚡️ v4o" description="Identifies Name, Email, Order ID." color="#ede9fe" borderColor="#7c3aed"/>
                        <FlowArrow/>
                        <FlowBox title="Structured Output" description="(JSON for next step)" color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                </div>

                <div className="workflow-example">
                    <h4>3. Simple Content Summarizer</h4>
                    <p><strong>Goal:</strong> Generate a short summary of a provided text document.</p>
                    <div className="workflow-diagram basic-linear">
                        <FlowBox title="Upload Document" description="(.txt, .docx)" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Summarization Agent" model="🧠 v5-Turbo" description="Reads document, generates concise summary." color="#fef3c7" borderColor="#d97706"/>
                        <FlowArrow/>
                        <FlowBox title="Display Summary" description="Shows generated summary." color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                </div>

                <div className="workflow-example">
                    <h4>4. Basic Language Translation</h4>
                    <p><strong>Goal:</strong> Translate user input from one language to another.</p>
                    <div className="workflow-diagram basic-linear">
                        <FlowBox title="User Input (French)" description="'Bonjour le monde'" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Translation Agent" model="⚡️ v4o / 🧠 v5-Turbo" description="Prompt: 'Translate to English'." color="#dbeafe" borderColor="#2563eb"/>
                        <FlowArrow/>
                        <FlowBox title="Translated Output" description="'Hello world'" color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                </div>

                <div className="workflow-example">
                    <h4>5. Sentiment Analysis Bot</h4>
                    <p><strong>Goal:</strong> Classify customer feedback as Positive, Negative, or Neutral.</p>
                    <div className="workflow-diagram basic-linear">
                        <FlowBox title="Customer Feedback" description="(e.g., Survey response)" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Sentiment Agent" model="⚡️ v4o" description="Prompt: 'Classify sentiment'." color="#e0f2fe" borderColor="#0ea5e9"/>
                        <FlowArrow/>
                        <FlowBox title="Classification Result" description="(e.g., 'Negative')" color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                </div>

                 <div className="workflow-example">
                    <h4>6. Simple Content Idea Generator</h4>
                    <p><strong>Goal:</strong> Brainstorm blog post titles based on a topic.</p>
                    <div className="workflow-diagram basic-linear">
                        <FlowBox title="Input Topic" description="'AI in Marketing'" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Brainstorming Agent" model="🧠 v5-Turbo" description="Prompt: 'Generate 5 catchy blog titles'." color="#fef9c3" borderColor="#ca8a04"/>
                        <FlowArrow/>
                        <FlowBox title="List of Titles" description="Outputs generated ideas." color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                </div>

                <div className="workflow-example">
                    <h4>7. Basic Code Explanation</h4>
                    <p><strong>Goal:</strong> Explain a snippet of code in plain language.</p>
                    <div className="workflow-diagram basic-linear">
                        <FlowBox title="Code Snippet" description="(e.g., Python function)" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Code Explainer Agent" model="🧠 v5-Turbo" description="Prompt: 'Explain this code step-by-step'." color="#e0e7ff" borderColor="#4f46e5"/>
                        <FlowArrow/>
                        <FlowBox title="Plain Language Explanation" description="Outputs explanation." color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                </div>

                 <div className="workflow-example">
                    <h4>8. Simple Email Drafting</h4>
                    <p><strong>Goal:</strong> Draft a polite follow-up email based on bullet points.</p>
                    <div className="workflow-diagram basic-linear">
                        <FlowBox title="Input Bullet Points" description="Key points for email" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Email Draft Agent" model="🧠 v5-Turbo" description="Prompt: 'Draft professional email'." color="#dcfce7" borderColor="#16a34a"/>
                        <FlowArrow/>
                        <FlowBox title="Draft Email Text" description="Ready for review/sending." color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                </div>

                 <div className="workflow-example">
                    <h4>9. Keyword Extraction</h4>
                    <p><strong>Goal:</strong> Identify main keywords from a block of text.</p>
                    <div className="workflow-diagram basic-linear">
                        <FlowBox title="Article Text" description="Input text content" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Keyword Agent" model="⚡️ v4o" description="Prompt: 'Extract top 5 keywords'." color="#fce7f3" borderColor="#db2777"/>
                        <FlowArrow/>
                        <FlowBox title="List of Keywords" description="Outputs keywords." color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                </div>

                <div className="workflow-example">
                    <h4>10. Basic Task Prioritization Suggestion</h4>
                    <p><strong>Goal:</strong> Suggest task order based on urgency/importance descriptions.</p>
                    <div className="workflow-diagram basic-linear">
                        <FlowBox title="List of Tasks & Notes" description="'Fix Bug (High)', 'Write Report (Medium)'" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Prioritization Agent" model="⚡️ v4o / 🧠 v5-Turbo" description="Analyzes input, suggests order." color="#fae8ff" borderColor="#a21caf"/>
                        <FlowArrow/>
                        <FlowBox title="Suggested Priority List" description="Outputs ordered tasks." color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                </div>


                {/* --- Intermediate Workflow Examples (11-20) --- */}
                <h3 style={{marginTop:'3rem', borderTop:'2px solid #e5e7eb', paddingTop:'2rem'}}>II. Intermediate Workflows (Branching, Simple Integration, Multi-Step)</h3>

                <div className="workflow-example">
                    <h4>11. Customer Support Triage & Routing</h4>
                    <p><strong>Goal:</strong> Route customer queries to the correct department (FAQ Bot or basic human escalation).</p>
                    <div className="workflow-diagram">
                        <FlowBox title="User Initiates Chat" description="(Via Website Embed)" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Agent 1: Triage Bot" model="⚡️ v4o" description="Understands intent (FAQ? Problem?)." color="#e0e7ff" borderColor="#4f46e5"/>
                        <FlowArrow/>
                         <div className="workflow-label">▼ Intent Analysis ▼</div>
                         <div className="flowchart-branching">
                            <div className="flowchart-path">
                                 <span className="flowchart-branch-label">→ FAQ Query</span>
                                 <FlowArrow/>
                                 <FlowBox title="Agent 2: FAQ Bot" model="⚡️ v4o" description="Answers directly using KB." color="#dbeafe" borderColor="#2563eb"/>
                                 <FlowArrow/>
                                 <FlowBox title="Resolution" color="#f0fdf4" borderColor="#16a34a"/>
                            </div>
                            <div className="flowchart-path">
                                 <span className="flowchart-branch-label">→ Needs Human</span>
                                 <FlowArrow/>
                                 <FlowBox title="Create Ticket" description="(Simple API Call to Helpdesk)" color="#fee2e2" borderColor="#dc2626" icon="🎫"/>
                                 <FlowArrow/>
                                 <FlowBox title="Inform User" description="'Ticket created, agent will contact.'" color="#feefc7" borderColor="#d97706"/>
                            </div>
                        </div>
                    </div>
                </div>

                 <div className="workflow-example">
                    <h4>12. Lead Qualification & CRM Update</h4>
                    <p><strong>Goal:</strong> Qualify website leads based on form input and update CRM.</p>
                    <div className="workflow-diagram">
                        <FlowBox title="Website Form Submission" description="(Name, Email, Company Size, Needs)" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Lead Scoring Agent" model="⚡️ v4o" description="Applies rules (e.g., Size > 50 = Hot)." color="#e0e7ff" borderColor="#4f46e5"/>
                        <FlowArrow/>
                        <div className="workflow-label">▼ Score Analysis ▼</div>
                         <div className="flowchart-branching">
                            <div className="flowchart-path">
                                 <span className="flowchart-branch-label">→ Qualified Lead ('Hot')</span>
                                 <FlowArrow/>
                                 <FlowBox title="Create/Update CRM Record" description="(API Call to Salesforce/HubSpot)" color="#dcfce7" borderColor="#16a34a" icon="📊"/>
                                 <FlowArrow/>
                                 <FlowBox title="Notify Sales Rep" description="(Internal Alert/Email)" color="#dbeafe" borderColor="#2563eb" icon="🔔"/>
                            </div>
                            <div className="flowchart-path">
                                 <span className="flowchart-branch-label">→ Not Qualified ('Cold')</span>
                                 <FlowArrow/>
                                 <FlowBox title="Add to Nurture List" description="(API Call to Marketing Automation)" color="#fef3c7" borderColor="#d97706" icon="📧"/>
                                 <FlowArrow/>
                                 <FlowBox title="Log Interaction" description="(Internal Database)" color="#f3f4f6" borderColor="#6b7280"/>
                            </div>
                        </div>
                    </div>
                </div>

                 <div className="workflow-example">
                    <h4>13. Automated Document Analysis Pipeline</h4>
                    <p><strong>Goal:</strong> Extract data, analyze sentiment, summarize documents.</p>
                    <div className="workflow-diagram">
                        <FlowBox title="Document Upload" description="(e.g., S3 Bucket)" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                         <FlowBox title="Agent 1: Data Extractor" model="⚡️ v4o" description="Extracts key fields." color="#e0e7ff" borderColor="#4f46e5"/>
                         <FlowArrow/>
                         <FlowBox title="Structured Data" description="(JSON)" color="#f3f4f6" borderColor="#6b7280"/>
                        <div className="flowchart-branching parallel"> {/* Added class for potential mobile styling */}
                            <div className="flowchart-path">
                                <FlowArrow/>
                                <FlowBox title="Agent 2: Sentiment Analyzer" model="⚡️ v4o" description="Classifies tone." color="#dbeafe" borderColor="#2563eb"/>
                            </div>
                            <div className="flowchart-path">
                                 <FlowArrow/>
                                 <FlowBox title="Agent 3: Summarizer" model="🧠 v5-Turbo" description="Generates summary." color="#fef3c7" borderColor="#d97706"/>
                            </div>
                        </div>
                         <div className="workflow-merge"> {/* Added class */}
                             <FlowArrow/> {/* Conceptual merge arrow */}
                         </div>
                        <FlowBox title="Consolidated Analysis Output" description="(Report/DB Update)" color="#f0fdf4" borderColor="#16a34a"/>
                     </div>
                </div>

                <div className="workflow-example">
                    <h4>14. Meeting Minutes Generator</h4>
                    <p><strong>Goal:</strong> Transcribe audio, identify speakers, summarize key decisions and action items.</p>
                     <div className="workflow-diagram">
                        <FlowBox title="Meeting Audio Upload" description="(.mp3, .wav)" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Speech-to-Text Service" description="(External API e.g., Whisper)" color="#f5f3ff" borderColor="#8b5cf6" icon="🎙️"/>
                        <FlowArrow/>
                        <FlowBox title="Raw Transcript" description="Text with timestamps" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Minutes Agent" model="🧠 v5-Turbo" description="Identifies speakers, extracts decisions & actions, formats." color="#e0e7ff" borderColor="#4f46e5"/>
                        <FlowArrow/>
                        <FlowBox title="Formatted Meeting Minutes" description="Ready for distribution." color="#f0fdf4" borderColor="#16a34a"/>
                     </div>
                </div>

                 <div className="workflow-example">
                    <h4>15. Social Media Monitoring & Response Draft</h4>
                    <p><strong>Goal:</strong> Monitor brand mentions, classify sentiment, draft appropriate reply.</p>
                     <div className="workflow-diagram">
                        <FlowBox title="New Brand Mention Alert" description="(e.g., via Monitoring Tool API)" color="#f3f4f6" borderColor="#6b7280" icon="🚨"/>
                        <FlowArrow/>
                         <FlowBox title="Mention Processing Agent" model="⚡️ v4o" description="Classifies sentiment (Pos/Neg/Neu). Extracts key topic." color="#e0f2fe" borderColor="#0ea5e9"/>
                        <FlowArrow/>
                         <div className="workflow-label">▼ Sentiment Analysis ▼</div>
                         <div className="flowchart-branching">
                            <div className="flowchart-path">
                                 <span className="flowchart-branch-label">→ Positive/Neutral</span>
                                 <FlowArrow/>
                                 <FlowBox title="Draft 'Thank You' Agent" model="⚡️ v4o" description="Generates standard positive reply draft." color="#dcfce7" borderColor="#16a34a"/>
                            </div>
                            <div className="flowchart-path">
                                 <span className="flowchart-branch-label">→ Negative</span>
                                 <FlowArrow/>
                                 <FlowBox title="Draft Empathetic Reply Agent" model="🧠 v5-Turbo" description="Generates helpful/apologetic reply draft." color="#fef3c7" borderColor="#d97706"/>
                            </div>
                        </div>
                         <div className="workflow-merge">
                             <FlowArrow/>
                         </div>
                         <FlowBox title="Send Draft to Social Team" description="(Slack/Email Alert with Draft)" color="#dbeafe" borderColor="#2563eb" icon="💬"/>
                     </div>
                </div>

                <div className="workflow-example">
                    <h4>16. Research Assistant with Source Linking</h4>
                    <p><strong>Goal:</strong> Answer questions based on multiple research papers, providing citations.</p>
                    <div className="workflow-diagram">
                        <FlowBox title="User Research Question" description="'What are the effects of X on Y?'" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Research Agent" model="🧠 v5-Turbo" description="Queries dataset of research papers (PDFs)." color="#e0e7ff" borderColor="#4f46e5"/>
                        <FlowArrow/>
                        <FlowBox title="Synthesized Answer + Citations" description="Provides answer and lists source documents/chunks." color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                    {/* Note: Assumes dataset preprocessing handles source tracking */}
                </div>

                <div className="workflow-example">
                    <h4>17. Personalized Email Marketing Drafts</h4>
                    <p><strong>Goal:</strong> Generate email drafts tailored to customer segments based on CRM data.</p>
                    <div className="workflow-diagram">
                        <FlowBox title="Trigger: New Campaign" description="Target Segment: 'High Value Customers'" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Fetch Customer Data" description="(CRM API Call - Purchase History, Interests)" color="#f5f3ff" borderColor="#8b5cf6" icon="📊"/>
                        <FlowArrow/>
                         {/* Conceptually looping through customers */}
                        <div className="workflow-label">▼ For Each Customer ▼</div>
                        <FlowArrow/>
                        <FlowBox title="Personalization Agent" model="🧠 v5-Turbo" description="Uses customer data + campaign brief to draft email." color="#fef3c7" borderColor="#d97706"/>
                        <FlowArrow/>
                        <FlowBox title="Add Draft to Marketing Tool" description="(API Call to Mailchimp/etc.)" color="#dcfce7" borderColor="#16a34a" icon="📧"/>
                    </div>
                </div>

                 <div className="workflow-example">
                    <h4>18. HR Policy Question Answering</h4>
                    <p><strong>Goal:</strong> Answer employee questions based on HR policy documents, escalating complex cases.</p>
                    <div className="workflow-diagram">
                         <FlowBox title="Employee Question" description="(via Intranet Chatbot)" color="#f3f4f6" borderColor="#6b7280"/>
                         <FlowArrow/>
                         <FlowBox title="Policy Bot" model="⚡️ v4o" description="Searches HR policy dataset." color="#e0e7ff" borderColor="#4f46e5"/>
                         <FlowArrow/>
                         <div className="workflow-label">▼ Answer Found? ▼</div>
                          <div className="flowchart-branching">
                             <div className="flowchart-path">
                                  <span className="flowchart-branch-label">→ Yes</span>
                                  <FlowArrow/>
                                  <FlowBox title="Provide Answer" description="Displays policy information." color="#f0fdf4" borderColor="#16a34a"/>
                             </div>
                             <div className="flowchart-path">
                                  <span className="flowchart-branch-label">→ No / Ambiguous</span>
                                  <FlowArrow/>
                                  <FlowBox title="Escalate to HR Agent" model="🧠 v5-Turbo" description="Asks clarifying questions if needed." color="#fef3c7" borderColor="#d97706"/>
                                  <FlowArrow/>
                                   <FlowBox title="Create HR Ticket / Notify" description="(Helpdesk API / Slack)" color="#fee2e2" borderColor="#dc2626" icon="🎫"/>
                             </div>
                         </div>
                    </div>
                </div>

                <div className="workflow-example">
                    <h4>19. Product Recommendation Engine (Simple)</h4>
                    <p><strong>Goal:</strong> Suggest products based on user's stated preferences or past behavior.</p>
                    <div className="workflow-diagram">
                        <FlowBox title="User Input / Profile Data" description="'Looking for running shoes', Past purchase: Trail Shoes" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Recommendation Agent" model="⚡️ v4o / 🧠 v5-Turbo" description="Matches input to product catalog dataset/rules." color="#ede9fe" borderColor="#7c3aed"/>
                        <FlowArrow/>
                        <FlowBox title="Product Suggestions" description="Outputs list of relevant products." color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                </div>

                 <div className="workflow-example">
                    <h4>20. Code Review Assistant (Syntax & Style)</h4>
                    <p><strong>Goal:</strong> Provide basic feedback on code style, syntax errors, and potential simple issues.</p>
                    <div className="workflow-diagram">
                        <FlowBox title="Code Submission" description="(e.g., Git commit hook)" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Linting / Static Analysis" description="(External Tool - e.g., ESLint)" color="#f5f3ff" borderColor="#8b5cf6" icon="🔧"/>
                        <FlowArrow/>
                         <FlowBox title="Code Review Agent" model="⚡️ v4o" description="Analyzes linter output + code. Checks for common style issues based on rules." color="#e0e7ff" borderColor="#4f46e5"/>
                         <FlowArrow/>
                         <FlowBox title="Review Comments" description="Posts comments to PR / outputs report." color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                </div>


                {/* --- Advanced Workflow Examples (21-30) --- */}
                <h3 style={{marginTop:'3rem', borderTop:'2px solid #e5e7eb', paddingTop:'2rem'}}>III. Advanced Workflows (Multi-Agent Swarms, Complex Logic, Human-in-the-Loop)</h3>

                <div className="workflow-example">
                    <h4>21. Multi-Stage Customer Onboarding Assistant</h4>
                    <p><strong>Goal:</strong> Guide new customers through setup, providing relevant info and tasks at each stage.</p>
                    <div className="workflow-diagram complex">
                        <FlowBox title="New User Signup" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Agent 1: Welcome Bot" model="⚡️ v4o" description="Sends welcome message. Asks about primary goal." color="#e0e7ff" borderColor="#4f46e5"/>
                        <FlowArrow/>
                        <FlowBox title="Store User Goal" description="(Database Update)" color="#f5f3ff" borderColor="#8b5cf6" icon="💾"/>
                        <FlowArrow/>
                        {/* Branch based on Goal */}
                        <div className="workflow-label">▼ Goal Routing ▼</div>
                         <div className="flowchart-branching">
                             {/* Path A */}
                            <div className="flowchart-path">
                                 <span className="flowchart-branch-label">→ Goal A ('Integration')</span>
                                 <FlowArrow/>
                                 <FlowBox title="Agent 2a: Integration Guide" model="⚡️ v4o" description="Provides API docs link, setup steps (from KB)." color="#dbeafe" borderColor="#2563eb"/>
                                 <FlowArrow/>
                                 <FlowBox title="Trigger Setup Checklist" description="(In-App Task System)" color="#cffafe" borderColor="#0891b2" icon="✔️"/>
                            </div>
                             {/* Path B */}
                            <div className="flowchart-path">
                                 <span className="flowchart-branch-label">→ Goal B ('Reporting')</span>
                                  <FlowArrow/>
                                 <FlowBox title="Agent 2b: Reporting Guide" model="⚡️ v4o" description="Shows reporting features, links tutorials (from KB)." color="#dbeafe" borderColor="#2563eb"/>
                                  <FlowArrow/>
                                  <FlowBox title="Suggest Template Report" description="(Based on Role/Industry)" color="#cffafe" borderColor="#0891b2" icon="📊"/>
                            </div>
                        </div>
                        {/* Common Follow-up */}
                         <div className="workflow-merge"> <FlowArrow/> </div>
                         <FlowBox title="Follow-up Agent" model="⚡️ v4o" description="Checks in after 2 days. Offers further help." color="#fef3c7" borderColor="#d97706"/>
                    </div>
                </div>

                <div className="workflow-example">
                    <h4>22. Dynamic Knowledge Base Article Generation</h4>
                    <p><strong>Goal:</strong> Automatically draft KB articles based on recurring support issues lacking documentation.</p>
                     <div className="workflow-diagram complex">
                        <FlowBox title="Support Ticket Analysis" description="(Identify trends - e.g., >10 tickets on 'Topic X' w/o KB)" color="#f3f4f6" borderColor="#6b7280" icon="📈"/>
                        <FlowArrow/>
                         <FlowBox title="Gather Relevant Tickets" description="(Query Helpdesk API for 'Topic X')" color="#f5f3ff" borderColor="#8b5cf6" icon="🎫"/>
                         <FlowArrow/>
                         <FlowBox title="Agent 1: Summarizer Agent" model="🧠 v5-Turbo" description="Summarizes common problem & solutions from tickets." color="#e0e7ff" borderColor="#4f46e5"/>
                         <FlowArrow/>
                         <FlowBox title="Agent 2: KB Draft Agent" model="🧠 v5-Turbo" description="Writes draft article based on summary + style guide." color="#fef3c7" borderColor="#d97706"/>
                         <FlowArrow/>
                         <FlowBox title="Submit Draft for Human Review" description="(To Content Team via CMS/Task)" color="#fee2e2" borderColor="#dc2626" icon="✍️"/>
                         <FlowArrow/>
                         <FlowBox title="Human Editor" description="Reviews, edits, publishes." color="#f3f4f6" borderColor="#6b7280"/>
                         <FlowArrow/>
                          <FlowBox title="Published KB Article" description="Available for future queries." color="#f0fdf4" borderColor="#16a34a"/>
                     </div>
                </div>

                <div className="workflow-example">
                    <h4>23. AI-Powered Sales Outreach & Follow-Up Swarm</h4>
                    <p><strong>Goal:</strong> Research prospects, personalize outreach, handle initial replies, schedule meetings.</p>
                    <div className="workflow-diagram complex">
                        <FlowBox title="New Prospect Added" description="(From Lead List / CRM)" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Agent 1: Prospect Researcher" model="⚡️ v4o / 🧠 v5-Turbo" description="Gathers info (LinkedIn, Website API, News Mentions)." color="#e0e7ff" borderColor="#4f46e5" icon="🔍"/>
                        <FlowArrow/>
                         <FlowBox title="Agent 2: Personalization Bot" model="🧠 v5-Turbo" description="Drafts personalized email using research + templates." color="#fef3c7" borderColor="#d97706" icon="📧"/>
                         <FlowArrow/>
                         <FlowBox title="Send Email via Sales Tool" description="(API call)" color="#f5f3ff" borderColor="#8b5cf6"/>
                         <FlowArrow/>
                          <div className="workflow-label">▼ Wait for Reply ▼</div>
                         <FlowArrow/>
                         {/* Reply Handling Branch */}
                          <FlowBox title="Prospect Reply Received" description="(Parse Email)" color="#f3f4f6" borderColor="#6b7280"/>
                          <FlowArrow/>
                           <FlowBox title="Agent 3: Reply Analyzer" model="⚡️ v4o" description="Classifies intent (Interested, Not Interested, Question)." color="#dbeafe" borderColor="#2563eb"/>
                           <FlowArrow/>
                           <div className="flowchart-branching">
                               <div className="flowchart-path">
                                    <span className="flowchart-branch-label">→ Interested / Question</span>
                                    <FlowArrow/>
                                    <FlowBox title="Agent 4: Meeting Scheduler" model="⚡️ v4o / 🧠 v5-Turbo" description="Answers simple questions, proposes meeting times (checks Calendar API)." color="#ede9fe" borderColor="#7c3aed" icon="📅"/>
                                    <FlowArrow/>
                                     <FlowBox title="Notify Sales Rep + Update CRM" description="(Alert + API Call)" color="#dcfce7" borderColor="#16a34a" icon="🔔"/>
                               </div>
                               <div className="flowchart-path">
                                    <span className="flowchart-branch-label">→ Not Interested</span>
                                     <FlowArrow/>
                                    <FlowBox title="Update CRM Status" description="(API Call)" color="#fee2e2" borderColor="#dc2626"/>
                                     <FlowArrow/>
                                     <FlowBox title="Log Outcome" color="#f3f4f6" borderColor="#6b7280"/>
                               </div>
                           </div>
                    </div>
                </div>

                <div className="workflow-example">
                    <h4>24. Financial Report Anomaly Detection</h4>
                    <p><strong>Goal:</strong> Analyze financial data, compare against historical trends/rules, flag anomalies for human review.</p>
                    <div className="workflow-diagram complex">
                        <FlowBox title="Ingest Financial Data" description="(CSV Upload / DB Query)" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Data Preprocessing" description="(Clean, Format, Extract Key Metrics)" color="#f5f3ff" borderColor="#8b5cf6" icon="⚙️"/>
                        <FlowArrow/>
                        <FlowBox title="Agent 1: Trend Analysis Bot" model="⚡️ v4o / 🧠 v5-Turbo" description="Compares current data to historical averages/rules." color="#e0e7ff" borderColor="#4f46e5" icon="📈"/>
                        <FlowArrow/>
                         <div className="workflow-label">▼ Anomaly Check ▼</div>
                         <FlowArrow/>
                         <FlowBox title="Agent 2: Anomaly Reasoner" model="🧠 v5-Turbo" description="If anomaly detected, attempts to explain potential cause based on context/rules." color="#fef3c7" borderColor="#d97706"/>
                         <FlowArrow/>
                          <div className="flowchart-branching">
                               <div className="flowchart-path">
                                    <span className="flowchart-branch-label">→ Anomaly Flagged</span>
                                    <FlowArrow/>
                                    <FlowBox title="Generate Alert + Summary" description="Details of anomaly + AI's reasoning." color="#fee2e2" borderColor="#dc2626" icon="🚨"/>
                                    <FlowArrow/>
                                     <FlowBox title="Send to Finance Analyst" description="(Email/Dashboard)" color="#dbeafe" borderColor="#2563eb" icon="👨‍💼"/>
                               </div>
                               <div className="flowchart-path">
                                    <span className="flowchart-branch-label">→ No Anomaly</span>
                                     <FlowArrow/>
                                    <FlowBox title="Log Check Complete" description="(Audit Trail)" color="#f0fdf4" borderColor="#16a34a"/>
                               </div>
                           </div>
                    </div>
                </div>

                 <div className="workflow-example">
                    <h4>25. Automated Competitor Monitoring & Analysis</h4>
                    <p><strong>Goal:</strong> Scrape competitor websites/news, summarize changes, analyze impact.</p>
                     <div className="workflow-diagram complex">
                        <FlowBox title="Scheduled Trigger" description="(e.g., Daily)" color="#f3f4f6" borderColor="#6b7280" icon="⏰"/>
                        <FlowArrow/>
                         <FlowBox title="Web Scraping Service" description="(Scrapes target URLs - Pricing, Blog, News)" color="#f5f3ff" borderColor="#8b5cf6" icon="🕷️"/>
                         <FlowArrow/>
                         <FlowBox title="Agent 1: Change Detection Bot" model="⚡️ v4o" description="Compares current scrape to previous version. Identifies diffs." color="#e0e7ff" borderColor="#4f46e5"/>
                         <FlowArrow/>
                          <div className="workflow-label">▼ Changes Detected? ▼</div>
                          <FlowArrow/>
                           <FlowBox title="Agent 2: Summarizer & Analyzer" model="🧠 v5-Turbo" description="Summarizes changes. Analyzes potential impact based on rules/market data." color="#fef3c7" borderColor="#d97706"/>
                           <FlowArrow/>
                            <FlowBox title="Generate Competitor Intel Report" description="(Formatted Summary & Analysis)" color="#dbeafe" borderColor="#2563eb" icon="📄"/>
                            <FlowArrow/>
                            <FlowBox title="Distribute Report" description="(Email to Strategy Team / Post to Slack)" color="#f0fdf4" borderColor="#16a34a" icon="🚀"/>
                     </div>
                </div>

                 <div className="workflow-example">
                    <h4>26. Complex Technical Support with Debugging Swarm</h4>
                    <p><strong>Goal:</strong> Handle complex technical issues by gathering logs, analyzing, searching KBs, and suggesting solutions.</p>
                     <div className="workflow-diagram complex">
                        <FlowBox title="User Reports Complex Issue" description="(With error message/description)" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Agent 1: Initial Triage & Log Request" model="⚡️ v4o" description="Asks for specific logs/config files." color="#e0e7ff" borderColor="#4f46e5"/>
                        <FlowArrow/>
                        <FlowBox title="User Uploads Logs" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Agent 2: Log Analyzer" model="🧠 v5-Turbo" description="Parses logs, identifies error patterns, extracts key info." color="#fef3c7" borderColor="#d97706" icon="📜"/>
                        <FlowArrow/>
                         {/* Parallel Search */}
                        <div className="flowchart-branching parallel">
                            <div className="flowchart-path">
                                 <FlowArrow/>
                                 <FlowBox title="Agent 3a: KB Search Bot" model="⚡️ v4o" description="Searches internal KB/Docs based on error patterns." color="#dbeafe" borderColor="#2563eb" icon="📚"/>
                            </div>
                            <div className="flowchart-path">
                                 <FlowArrow/>
                                 <FlowBox title="Agent 3b: Public Forum Search (Optional)" model="⚡️ v4o" description="Searches Stack Overflow/etc. (via API)." color="#ede9fe" borderColor="#7c3aed" icon="🌐"/>
                            </div>
                        </div>
                         <div className="workflow-merge"> <FlowArrow/> </div>
                         <FlowBox title="Agent 4: Solution Synthesizer" model="🧠 v5-Turbo" description="Combines log analysis + search results. Proposes solution steps." color="#e0e7ff" borderColor="#4f46e5"/>
                         <FlowArrow/>
                          <FlowBox title="Present Solution / Escalate" description="If confident, gives steps. If not, escalates to L2 Support with summary." color="#f0fdf4" borderColor="#16a34a"/>
                     </div>
                </div>

                <div className="workflow-example">
                    <h4>27. Personalized Learning Path Generator</h4>
                    <p><strong>Goal:</strong> Create a custom learning plan based on user's goals, current knowledge, and preferred learning style.</p>
                    <div className="workflow-diagram complex">
                        <FlowBox title="User Assessment / Goal Input" description="('Learn Python', 'Beginner', 'Video Learner')" color="#f3f4f6" borderColor="#6b7280"/>
                        <FlowArrow/>
                        <FlowBox title="Learning Path Agent" model="🧠 v5-Turbo" description="Uses input + dataset of learning resources (courses, videos, articles tagged by topic/level/style)." color="#e0e7ff" borderColor="#4f46e5" icon="🗺️"/>
                        <FlowArrow/>
                        <FlowBox title="Generate Personalized Plan" description="(Sequence of modules/resources with links)" color="#dcfce7" borderColor="#16a34a" icon="🎓"/>
                        <FlowArrow/>
                        <FlowBox title="Display Plan to User" description="(In LMS or webpage)" color="#f0fdf4" borderColor="#16a34a"/>
                    </div>
                </div>

                <div className="workflow-example">
                    <h4>28. AI-Assisted Creative Writing Partner</h4>
                    <p><strong>Goal:</strong> Collaborate with a human writer, offering plot suggestions, character ideas, or overcoming writer's block.</p>
                    <div className="workflow-diagram complex interactive">
                         <FlowBox title="Human Writer Input" description="(Current scene / 'I'm stuck on dialogue')" color="#f3f4f6" borderColor="#6b7280" icon="✍️"/>
                         {/* Loop Arrow */}
                         <svg width="70" height="20" viewBox="0 0 100 30" fill="none" xmlns="http://www.w3.org/2000/svg" className="workflow-loop-arrow down">
                             <path d="M50 0 V15 C 50 25, 70 25, 70 15 V0" stroke="#9ca3af" strokeWidth="1.5" strokeDasharray="3 2"/>
                             <path d="M70 15 L 75 10 M 70 15 L 65 10" stroke="#9ca3af" strokeWidth="1.5"/>
                         </svg>
                         <FlowBox title="Creative Agent" model="🧠 v5-Turbo" description="Analyzes context, provides suggestions (dialogue options, plot twists, descriptions) based on prompt." color="#fef3c7" borderColor="#d97706" icon="💡"/>
                         {/* Loop Arrow Back */}
                          <svg width="70" height="20" viewBox="0 0 100 30" fill="none" xmlns="http://www.w3.org/2000/svg" className="workflow-loop-arrow up">
                             <path d="M50 30 V15 C 50 5, 30 5, 30 15 V30" stroke="#9ca3af" strokeWidth="1.5" strokeDasharray="3 2"/>
                             <path d="M30 15 L 25 20 M 30 15 L 35 20" stroke="#9ca3af" strokeWidth="1.5"/>
                         </svg>
                         <FlowBox title="Writer Reviews & Incorporates" description="Uses AI suggestions or asks for refinement." color="#f3f4f6" borderColor="#6b7280"/>
                     </div>
                     <p className="workflow-loop-label"><em>(Iterative Human-AI Loop)</em></p>
                </div>

                <div className="workflow-example">
                    <h4>29. Proactive System Monitoring & Predictive Maintenance</h4>
                    <p><strong>Goal:</strong> Monitor system logs/metrics, predict potential failures, create maintenance tickets.</p>
                    <div className="workflow-diagram complex">
                        <FlowBox title="Stream System Metrics/Logs" description="(e.g., Prometheus, ELK Stack)" color="#f3f4f6" borderColor="#6b7280" icon="📡"/>
                        <FlowArrow/>
                        <FlowBox title="Agent 1: Anomaly Detection" model="⚡️ v4o" description="Identifies deviations from normal patterns." color="#e0e7ff" borderColor="#4f46e5" icon="📊"/>
                        <FlowArrow/>
                         <FlowBox title="Agent 2: Predictive Failure Modeler" model="🧠 v5-Turbo / Specialized ML" description="Analyzes anomalies + historical failure data to predict likelihood of failure." color="#fef3c7" borderColor="#d97706" icon="🔮"/>
                        <FlowArrow/>
                         <div className="workflow-label">▼ High Failure Probability? ▼</div>
                         <FlowArrow/>
                         <FlowBox title="Create Preventative Maintenance Ticket" description="(Jira/ServiceNow API Call with details)" color="#fee2e2" borderColor="#dc2626" icon="🎫"/>
                         <FlowArrow/>
                         <FlowBox title="Notify Ops Team" description="(Slack/PagerDuty Alert)" color="#dbeafe" borderColor="#2563eb" icon="🔔"/>
                    </div>
                </div>

                 <div className="workflow-example">
                    <h4>30. Fully Automated End-to-End Order Processing & Fulfillment</h4>
                    <p><strong>Goal:</strong> Process incoming orders, check inventory, handle payment, initiate shipping, and send notifications.</p>
                    <div className="workflow-diagram complex">
                         <FlowBox title="New Order Received" description="(Webhook from E-commerce Platform)" color="#f3f4f6" borderColor="#6b7280" icon="🛒"/>
                         <FlowArrow/>
                         <FlowBox title="Agent 1: Order Validator" model="⚡️ v4o" description="Checks order format, item validity." color="#e0e7ff" borderColor="#4f46e5"/>
                         <FlowArrow/>
                          <FlowBox title="Check Inventory" description="(Query Inventory DB/API)" color="#f5f3ff" borderColor="#8b5cf6" icon="📦"/>
                         <FlowArrow/>
                          <div className="workflow-label">▼ Stock Available? ▼</div>
                          <div className="flowchart-branching">
                             <div className="flowchart-path">
                                 <span className="flowchart-branch-label">→ Yes</span>
                                 <FlowArrow/>
                                 <FlowBox title="Process Payment" description="(Call Stripe/PayPal API)" color="#ede9fe" borderColor="#7c3aed" icon="💳"/>
                                 <FlowArrow/>
                                  <FlowBox title="Agent 2: Fulfillment Agent" model="⚡️ v4o" description="Generates shipping label request (Shipping API)." color="#dbeafe" borderColor="#2563eb" icon="🚚"/>
                                  <FlowArrow/>
                                  <FlowBox title="Update Order Status" description="(E-commerce Platform API)" color="#cffafe" borderColor="#0891b2"/>
                                  <FlowArrow/>
                                  <FlowBox title="Agent 3: Notification Agent" model="⚡️ v4o" description="Sends 'Order Shipped' Email/SMS." color="#dcfce7" borderColor="#16a34a" icon="✉️"/>
                             </div>
                             <div className="flowchart-path">
                                 <span className="flowchart-branch-label">→ No</span>
                                  <FlowArrow/>
                                  <FlowBox title="Notify Customer of Delay" description="(Email via Agent 3)" color="#fef3c7" borderColor="#d97706" icon="⏳"/>
                                  <FlowArrow/>
                                  <FlowBox title="Flag for Manual Review" description="(Alert Purchasing Team)" color="#fee2e2" borderColor="#dc2626" icon="🚩"/>
                             </div>
                         </div>
                     </div>
                </div>


                 <ProTip title="Designing Robust Workflows" icon="🛠️">
                     <ul>
                         <li><strong>Start Simple, Iterate:</strong> Don't try to build the most complex workflow first. Automate one part, test it, then add the next layer.</li>
                         <li><strong>Clear Agent Roles:</strong> Define specific, non-overlapping responsibilities for each agent in a swarm.</li>
                         <li><strong>Error Handling is Crucial:</strong> What happens if an API fails? If an agent gives a bad response? Plan for failures and add fallback paths or human escalation points.</li>
                         <li><strong>Monitor & Optimize:</strong> Track workflow performance (time, cost, success rate). Identify bottlenecks and refine agent prompts, models, or integration logic.</li>
                         <li><strong>Leverage External Tools:</strong> Foundry agents are powerful, but don't reinvent the wheel. Integrate with specialized services (payment, shipping, CRM, transcription) via APIs.</li>
                         <li><strong>Consider Costs:</strong> Complex workflows with multiple v5-Turbo calls can be expensive. Use faster/cheaper models (v4o) wherever possible for triage, simple extraction, or routing.</li>
                     </ul>
                 </ProTip>

                 <div className="info-box">
                    <h4>From Blueprints to Automated Reality</h4>
                    <p>These 30 examples are just the beginning. By combining specialized Foundry agents, conditional logic, external integrations, and human oversight where needed, you can design intelligent systems that transform how your business operates. Think modularly, build iteratively, and unlock the true potential of orchestrated AI.</p>
                 </div>
            </div>
        )
    },

  }; // End of pageContent

  // --- Search Logic ---
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) {
      return [];
    }
    const lowerSearchTerm = searchTerm.toLowerCase();
    return Object.entries(pageContent)
      .map(([key, page]) => {
        const titleMatch = page.title.toLowerCase().includes(lowerSearchTerm);
        // Search simplified text field for content matches
        const contentMatch = page.searchableText?.toLowerCase().includes(lowerSearchTerm) ?? false;

        if (titleMatch || contentMatch) {
          return { key, title: page.title, matchType: titleMatch ? 'Title' : 'Content' };
        }
        return null;
      })
      .filter(result => result !== null);
  }, [searchTerm, pageContent]);


  // Render the active page content or search results
  const renderMainArea = () => {
    if (searchTerm.trim()) {
      // --- Render Search Results View ---
      return (
        <div className="content search-results-content page-enter-active">
          <h2>Search Results for "{searchTerm}"</h2>
          {searchResults.length > 0 ? (
            <ul className="search-results-list">
              {searchResults.map(result => (
                <li key={result.key} className="search-result-item" onClick={() => handleResultClick(result.key)}>
                  <span className="search-result-title">{result.title}</span>
                  {/* <span className="search-result-match-type">Match in: {result.matchType}</span> */}
                </li>
              ))}
            </ul>
          ) : (
            <p>No results found. Try refining your search terms.</p>
          )}
           <button onClick={clearSearch} className="clear-search-button">Clear Search</button>
        </div>
      );
    } else {
      // --- Render Normal Page Content ---
      const page = pageContent[activePage] || pageContent['introduction'];
      const pageNeedsProgressBar = ['datasetCreation', 'agentCreation', 'embeddingAgents', 'scalingAgents'].includes(activePage);
      let progressBar = null;
      let mainContentElement = page.content;

      if (page.content && React.isValidElement(page.content)) {
          const childrenArray = React.Children.toArray(page.content.props?.children);
          const firstChild = childrenArray[0];
          if (pageNeedsProgressBar && React.isValidElement(firstChild) && firstChild.type === QuestProgressBar) {
              progressBar = firstChild;
              mainContentElement = React.cloneElement(page.content, {}, childrenArray.slice(1));
          }
      } else if (page.content){
          // Wrap string content in a div if needed, though usually it's JSX
          mainContentElement = typeof page.content === 'string' ? <div>{page.content}</div> : page.content;
      } else {
          mainContentElement = <div>Content not available.</div>;
      }

      return (
        <div className={`content ${isMounted ? 'page-enter-active' : ''}`}>
          {progressBar} {/* Render progress bar outside the h2 */}
          <h2>{page.title}</h2>
          {mainContentElement} {/* Render the rest of the content */}
        </div>
      );
    }
  };


  return (
    <div className={`docs-container ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Overlay for mobile sidebar */}
      {isSidebarOpen && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}

      {/* Header */}
      <header className="header">
           {/* Hamburger Menu Button - Mobile Only */}
           <button className="hamburger-btn" onClick={toggleSidebar} aria-label="Toggle Menu">
                <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
           </button>

           <div className="logo">
           <div className="logo-icon">
             <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{width: '22px', height: '22px', color: '#6366f1'}}> {/* Scaled icon */}
                <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.546L9.1 6.112H5.907c-.917 0-1.699.663-1.85 1.546L3.74 9.302l.001.017.01.095.022.191.034.286.047.381.06.476.075.57.09.665.106.759.124.852.142.944.16 1.034.18.112.199.116.219.119.24.121.26.121.282.119.304.116.327.112.349.104.372.095.396.084.419.072.442.059.465.045.488.03.51.015L12 21.75l4.55-8.663a1.023 1.023 0 0 0 .51-.015l.488-.03.465-.045.442-.059.419-.072.396-.084.372-.095.349-.104.327-.112.304-.116.282-.119.26-.121.24-.121.219-.119.199-.116.18-.112.16-1.034.142-.944.124-.852.106-.759.09-.665.075-.57.06-.476.047-.381.034-.286.022-.191.01-.095L20.26 9.3l-.31-1.646a1.875 1.875 0 0 0-1.85-1.546h-3.203l-.128-2.316a1.875 1.875 0 0 0-1.85-1.546h-1.83Z" clipRule="evenodd" />
             </svg>
           </div>
          <h1>Echidna AI Foundry</h1>
        </div>
        <div className="nav-tabs">
           <div className={`tab ${['introduction', 'datasetCreation', 'agentCreation', 'choosingModel', 'embeddingAgents', 'scalingAgents', 'agentSwarm', 'abTesting', 'apiUsage', 'aiWorkflows'].includes(activePage) && !searchTerm ? 'active' : ''}`} onClick={() => { handleResultClick('introduction'); }}>Docs</div>

          <div className={`tab ${activePage === 'foundrySim' && !searchTerm ? 'active' : ''}`} onClick={() => { handleResultClick('foundrySim'); }}>Simulations</div>
          <div className="tab disabled">Community</div> {/* Placeholder */}
        </div>
        <div className="search-box">
          <input
            type="text"
            placeholder="Search..." // Shortened placeholder for mobile
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchTerm && (
             <button onClick={clearSearch} className="clear-search-btn">
                <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd"></path></svg>
             </button>
          )}
          <button className="search-btn" onClick={() => {/* Maybe trigger search explicitly? Currently searches live */}}>
             <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          </button>
        </div>
      </header>

      <div className="docs-main">
        {/* Sidebar Navigation */}
        <aside className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>

          {/* Getting Started Section */}
          <div className="section">
            <div className="section-title" onClick={() => toggleSection('gettingStarted')}>
              <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" className={`expansion-arrow ${expandedSections.gettingStarted ? 'expanded' : ''}`}><polyline points="6 9 12 15 18 9"></polyline></svg>
              <span>Getting Started</span>
            </div>
            {expandedSections.gettingStarted && (
              <div className="section-items slide-down">
                <div className={`section-item ${activePage === 'introduction' && !searchTerm ? 'active' : ''}`} onClick={() => handleResultClick('introduction')}>⚔️ Foundry Quest Intro</div>
              </div>
            )}
          </div>

          {/* Building Agents Section */}
          <div className="section">
            <div className="section-title" onClick={() => toggleSection('buildingAgents')}>
               <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" className={`expansion-arrow ${expandedSections.buildingAgents ? 'expanded' : ''}`}><polyline points="6 9 12 15 18 9"></polyline></svg>
              <span>Building Assistants</span>
            </div>
            {expandedSections.buildingAgents && (
              <div className="section-items slide-down">
                <div className={`section-item ${activePage === 'datasetCreation' && !searchTerm ? 'active' : ''}`} onClick={() => handleResultClick('datasetCreation')}>📜 Lv 1: Datasets</div>
                <div className={`section-item ${activePage === 'agentCreation' && !searchTerm ? 'active' : ''}`} onClick={() => handleResultClick('agentCreation')}>✨ Lv 2: Agent Creation</div>
                <div className={`section-item ${activePage === 'choosingModel' && !searchTerm ? 'active' : ''}`} onClick={() => handleResultClick('choosingModel')}>🧠 Choosing AI Model</div>
              </div>
            )}
          </div>

          {/* Deployment & Usage Section */}
           <div className="section">
            <div className="section-title" onClick={() => toggleSection('deploymentUsage')}>
               <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" className={`expansion-arrow ${expandedSections.deploymentUsage ? 'expanded' : ''}`}><polyline points="6 9 12 15 18 9"></polyline></svg>
              <span>Deployment & Usage</span>
            </div>
            {expandedSections.deploymentUsage && (
              <div className="section-items slide-down">
                 <div className={`section-item ${activePage === 'embeddingAgents' && !searchTerm ? 'active' : ''}`} onClick={() => handleResultClick('embeddingAgents')}>🚀 Lv 3: Using Agents</div>
                 <div className={`section-item ${activePage === 'apiUsage' && !searchTerm ? 'active' : ''}`} onClick={() => handleResultClick('apiUsage')}>🔌 Using the Agent API</div>
                 <div className={`section-item ${activePage === 'foundrySim' && !searchTerm ? 'active' : ''}`} onClick={() => handleResultClick('foundrySim')}>🎮 Foundry Sim</div>
              </div>
            )}
          </div>


          {/* Scaling & Advanced Section */}
          <div className="section">
            <div className="section-title" onClick={() => toggleSection('scalingAdvanced')}>
              <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" className={`expansion-arrow ${expandedSections.scalingAdvanced ? 'expanded' : ''}`}><polyline points="6 9 12 15 18 9"></polyline></svg>
              <span>Scaling & Advanced</span>
            </div>
            {expandedSections.scalingAdvanced && (
              <div className="section-items slide-down">
                <div className={`section-item ${activePage === 'scalingAgents' && !searchTerm ? 'active' : ''}`} onClick={() => handleResultClick('scalingAgents')}>⚙️ Lv 4: Scaling & Optimizing</div>
                <div className={`section-item ${activePage === 'agentSwarm' && !searchTerm ? 'active' : ''}`} onClick={() => handleResultClick('agentSwarm')}>🌟 Agent Swarms</div>
                <div className={`section-item ${activePage === 'abTesting' && !searchTerm ? 'active' : ''}`} onClick={() => handleResultClick('abTesting')}>🧪 A/B Testing Agents</div>
                 <div className={`section-item disabled`} title="Coming Soon!">📊 Evaluation Metrics</div> {/* Example disabled */}
              </div>
            )}
          </div>

            {/* NEW Workflows Section */}
           <div className="section">
            <div className="section-title" onClick={() => toggleSection('workflows')}>
              <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" className={`expansion-arrow ${expandedSections.workflows ? 'expanded' : ''}`}><polyline points="6 9 12 15 18 9"></polyline></svg>
              <span>AI Infrastructure & Workflows</span>
            </div>
            {expandedSections.workflows && (
              <div className="section-items slide-down">
                <div className={`section-item ${activePage === 'aiWorkflows' && !searchTerm ? 'active' : ''}`} onClick={() => handleResultClick('aiWorkflows')}>🗺️ Designing Workflows</div>
                 {/* Add more pages here later if needed */}
              </div>
            )}
          </div>

        </aside>

        {/* Main content Area */}
        <main className="main-content">
           {/* Content rendered here (either page or search results) */}
           {renderMainArea()}
        </main>
      </div>

      {/* Styles */}
      {/* NOTE: CSS Includes mobile-specific overrides using @media query */}
      <style jsx>{`
        /* Base Styles & Font - Reduced Root Size */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        :root {
          font-size: 14px; /* Base font size reduction */
          --sidebar-width: 230px;
          --sidebar-width-mobile: 250px; /* Slightly wider for mobile tap targets */
          --header-height: 56px;
        }

        .docs-container {
            font-family: 'Inter', sans-serif;
            color: #374151;
            display: flex;
            flex-direction: column;
            height: 100vh;
            width: 100vw; /* Ensure full viewport width */
            overflow-x: hidden; /* Prevent horizontal scroll on main container */
            background-color: #f9fafb;
            font-size: 1rem; /* Use root size */
        }

        /* Header Styles - Scaled Down */
        .header {
            display: flex;
            align-items: center;
            padding: 0 1.3rem; /* Default padding */
            height: var(--header-height);
            background-color: #fff;
            border-bottom: 1px solid #e5e7eb;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
            flex-shrink: 0;
            position: sticky;
            top: 0;
            z-index: 50;
        }
        .logo { display: flex; align-items: center; margin-right: 1.8rem; gap: 0.5rem; flex-shrink: 0; }
        .logo-icon svg { transition: transform 0.3s ease-out; }
        .logo:hover .logo-icon svg { transform: rotate(15deg) scale(1.1); }
        .logo h1 { font-size: 1.2rem; font-weight: 700; margin: 0; color: #111827; white-space: nowrap; }

        .nav-tabs { display: flex; margin-right: auto; align-items: stretch; height: 100%; gap: 0.4rem; overflow-x: auto; /* Allow tabs to scroll on mobile */ }
        .tab { display: flex; align-items: center; padding: 0.4rem 0.9rem; cursor: pointer; border-radius: 5px 5px 0 0; font-weight: 500; color: #4b5563; border-bottom: 3px solid transparent; transition: all 0.2s ease-in-out; white-space: nowrap; font-size: 0.9em; margin-bottom: -1px; /* Align with border */ }
        .tab:hover { color: #4f46e5; background-color: #f0f0ff; }
        .tab.active { color: #4f46e5; border-bottom-color: #4f46e5; font-weight: 600; background-color: #eef2ff;}
        .tab a.link { padding: 0; border-bottom: none; display: inline-flex; align-items: center;}
        .tab a.link:hover { color: inherit; }
        .tab.disabled { color: #9ca3af; cursor: not-allowed; opacity: 0.7; }
        .tab.disabled:hover { background-color: transparent; color: #9ca3af; border-bottom-color: transparent;}

        /* Search Box - Scaled Down */
        .search-box { display: flex; align-items: center; background-color: #f3f4f6; border-radius: 6px; padding: 0.3rem 0.6rem; border: 1px solid #e5e7eb; transition: border-color 0.2s ease, box-shadow 0.2s ease; margin-left: 1rem; flex-shrink: 0; }
        .search-box:focus-within { border-color: #a5b4fc; box-shadow: 0 0 0 2px rgba(199, 210, 254, 0.6); }
        .search-box input { border: none; background: transparent; padding: 0.3rem; width: 180px; outline: none; color: #374151; font-size: 0.9em;}
        .search-btn, .clear-search-btn { background: transparent; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #6b7280; padding: 0 0.4rem; transition: color 0.2s ease;}
        .search-btn:hover, .clear-search-btn:hover { color: #4f46e5; }
        .clear-search-btn { margin-left: 0.2rem; } /* Spacing for clear button */

        /* Hamburger Menu Button - Mobile Only */
        .hamburger-btn {
          display: none; /* Hidden by default */
          background: none;
          border: none;
          padding: 0.5rem;
          margin-right: 0.8rem;
          cursor: pointer;
          color: #4b5563;
        }
        .hamburger-btn:hover { color: #4f46e5; }

        /* Main Layout Styles */
        .docs-main { display: flex; flex: 1; overflow: hidden; position: relative; /* Needed for sidebar positioning */}
        .sidebar {
          width: var(--sidebar-width);
          min-width: var(--sidebar-width);
          border-right: 1px solid #e5e7eb;
          overflow-y: auto;
          background-color: #fff;
          padding: 1.3rem 0;
          height: calc(100vh - var(--header-height));
          scrollbar-width: thin;
          scrollbar-color: #c7d2fe #fff;
          transition: transform 0.3s ease-in-out, min-width 0.3s ease-in-out, width 0.3s ease-in-out; /* Smooth transition */
        }
        .sidebar::-webkit-scrollbar { width: 7px; }
        .sidebar::-webkit-scrollbar-track { background: #fff; }
        .sidebar::-webkit-scrollbar-thumb { background-color: #c7d2fe; border-radius: 4px; border: 1.5px solid #fff; }
        .sidebar::-webkit-scrollbar-thumb:hover { background-color: #a5b4fc; }

        /* Mobile Sidebar Overlay */
        .sidebar-overlay {
            display: none; /* Hidden by default */
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.4);
            z-index: 90; /* Below sidebar, above content */
            cursor: pointer;
        }
        .docs-container.sidebar-open .sidebar-overlay {
            display: block; /* Show when sidebar is open on mobile */
        }


        /* Sidebar Section Styles */
        .section { margin-bottom: 0.4rem; }
        .section-title { display: flex; align-items: center; padding: 0.6rem 1.2rem; font-weight: 600; cursor: pointer; color: #374151; user-select: none; transition: background-color 0.2s ease, color 0.2s ease; border-left: 3px solid transparent; margin-right: 0.6rem; border-radius: 0 5px 5px 0;}
        .section-title:hover { background-color: #f4f6ff; color: #4338ca; }
        .section-title svg.expansion-arrow { margin-right: 0.6rem; transition: transform 0.25s ease-in-out; color: #9ca3af;}
        .section-title:hover svg.expansion-arrow { color: #6366f1;}
        .section-title svg.expansion-arrow.expanded { transform: rotate(90deg); color: #4f46e5; }
        .section-title span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 0.95em; }

        /* Sidebar Item Styles & Animation */
        .section-items {
            padding-left: calc(1.2rem + 14px); /* Adjusted indent */
            margin-top: 0.2rem;
            margin-bottom: 0.4rem;
            overflow: hidden;
        }
        .section-items.slide-down {
           animation: slideDown 0.3s ease-out forwards;
        }
        @keyframes slideDown {
            from { opacity: 0; transform: translateY(-8px); max-height: 0; }
            to { opacity: 1; transform: translateY(0); max-height: 500px; /* Adjust as needed */ }
        }

        .section-item { padding: 0.45rem 1rem; cursor: pointer; border-radius: 5px; margin: 0.2rem 0.6rem 0.2rem 0; color: #4b5563; font-size: 0.88rem; display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; border-left: 3px solid transparent; transition: all 0.15s ease-in-out; }
        .section-item:hover { background-color: #f4f6ff; color: #4338ca; transform: translateX(3px); }
        .section-item.active { background-color: #eef2ff; color: #4f46e5; font-weight: 600; border-left-color: #6366f1; }
        .section-item.disabled { color: #9ca3af; cursor: not-allowed; background-color: transparent !important; border-left-color: transparent !important; opacity: 0.6;}
        .section-item.disabled:hover { transform: none; color:#9ca3af; }

        /* Main Content Styles */
        .main-content {
          flex: 1;
          overflow-y: auto;
          height: calc(100vh - var(--header-height));
          background-color: #f9fafb;
          padding: 0; /* Padding moved to .content */
          scrollbar-width: thin;
          scrollbar-color: #c7d2fe #f9fafb;
          position: relative; /* Ensure it's part of flow */
        }
         .main-content::-webkit-scrollbar { width: 7px; }
         .main-content::-webkit-scrollbar-track { background: #f9fafb; }
         .main-content::-webkit-scrollbar-thumb { background-color: #c7d2fe; border-radius: 4px; border: 1.5px solid #f9fafb; }
         .main-content::-webkit-scrollbar-thumb:hover { background-color: #a5b4fc; }

        /* Content Wrapper for Padding and Animation */
        .content {
            max-width: 75ch;
            margin: 1.8rem auto;
            background-color: #fff;
            padding: 2.5rem 3rem;
            border-radius: 8px;
            box-shadow: 0 3px 9px rgba(0,0,0,0.07);
            opacity: 0;
            transform: translateY(10px);
            transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
        }
        .content.page-enter-active {
            opacity: 1;
            transform: translateY(0);
        }

        /* Content Typography & Elements - Scaled Down */
        .content h2 { font-size: 1.8rem; font-weight: 700; margin-top: 0.3rem; margin-bottom: 2rem; color: #111827; border-bottom: 1px solid #e5e7eb; padding-bottom: 1rem; letter-spacing: -0.02em; display: flex; align-items: center;}
        .content h3 { font-size: 1.5rem; font-weight: 600; margin-top: 2.5rem; margin-bottom: 1.3rem; color: #1f2937; border-bottom: 1px solid #f3f4f6; padding-bottom: 0.4rem;}
        .content h4 { font-size: 1.15rem; font-weight: 600; margin-top: 2rem; margin-bottom: 1rem; color: #374151; }
        .content p, .content li { margin-bottom: 1rem; line-height: 1.75; color: #4b5563; font-size: 0.9rem; /* Smaller base font */ }
        .content ul, .content ol { margin-bottom: 1.2rem; padding-left: 2rem; }
        .content ul { list-style: none; }
        .content ul li { position: relative; padding-left: 1.3em;}
        .content ul li::before { content: "◆"; color: #818cf8; font-weight: bold; display: inline-block; position: absolute; left: 0; top: 0.15em; font-size: 0.9em; transition: color 0.2s ease;}
        .content ul li:hover::before { color: #6366f1; }
        .content ol { list-style: none; counter-reset: list-counter;}
        .content ol li { counter-increment: list-counter; position: relative; padding-left: 1.8em;}
        .content ol li::before { content: counter(list-counter); background-color: #eef2ff; color: #4f46e5; font-weight: 600; display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; width: 1.6em; height: 1.6em; position: absolute; left: -0.2em; top: 0.15em; font-size: 0.7em; text-align: center; transition: background-color 0.2s ease, color 0.2s ease;}
        .content ol li:hover::before { background-color: #c7d2fe; color: #3730a3;}
        .content ul ul, .content ol ol, .content ul ol, .content ol ul { margin-top: 0.7rem; margin-bottom: 0.7rem; padding-left: 1.8rem; }
        .content li { margin-bottom: 0.7rem; }
        .content li > strong, .content p > strong { color: #1f2937; font-weight: 600; }
        .content li > code, .content p > code { font-size: 0.9em; } /* Adjusted inline code size */

        /* Link Styles - Scaled Down */
        .link { color: #4f46e5; cursor: pointer; text-decoration: none; font-weight: 500; border-bottom: 1.5px solid #a5b4fc; transition: border-bottom-color 0.2s ease, color 0.2s ease; padding-bottom: 1px; }
        .link:hover { border-bottom-color: #4f46e5; color: #3730a3; }
        .external-link {}
        .external-link::after { content: '↗'; display: inline-block; margin-left: 0.25em; font-size: 0.75em; opacity: 0.6; transition: opacity 0.2s ease, transform 0.2s ease; color: #4f46e5;}
        .external-link:hover::after { opacity: 1; transform: translateX(1px) translateY(-1px);}


        /* Info Box & ProTip Styles - Scaled Down */
        .info-box { background-color: #eef2ff; border-left: 4px solid #6366f1; padding: 1.3rem 1.6rem; margin: 2rem 0; border-radius: 6px; box-shadow: 0 2px 6px rgba(0,0,0,0.06); transition: transform 0.2s ease-out;}
        .info-box:hover { transform: translateY(-2px); box-shadow: 0 4px 10px rgba(99, 102, 241, 0.15);}
        .info-box h4 { margin-top: 0; margin-bottom: 0.7rem; color: #3730a3; font-size: 1.1rem; font-weight: 600; display: flex; align-items: center;}
        .info-box h4::before { content: "ℹ️"; margin-right: 0.5rem; font-size: 1em;}
        .info-box p { margin-bottom: 0.6rem; color: #4338ca; line-height: 1.7; font-size: 0.88rem; }
        .info-box p:last-child { margin-bottom: 0; }

        .pro-tip-box { margin: 2rem 0; padding: 1.1rem 1.4rem; border-left: 4px solid #f59e0b; background-color: #fffbeb; border-radius: 6px; box-shadow: 0 2px 6px rgba(0,0,0,0.06); transition: transform 0.2s ease-out;}
        .pro-tip-box:hover { transform: translateY(-2px); box-shadow: 0 4px 10px rgba(245, 158, 11, 0.15);}
        .pro-tip-title { margin-top: 0; margin-bottom: 0.6rem; color: #92400e; font-size: 1.1rem; font-weight: 600; display: flex; align-items: center;}
        .pro-tip-icon { margin-right: 0.5rem; font-size: 1.1em; display: inline-block; }
        .pro-tip-content { color: #92400e; line-height: 1.7; font-size: 0.88rem; }
        .pro-tip-content ul { padding-left: 1.2rem; margin-bottom: 0.4rem;}
        .pro-tip-content ul li::before { color: #f59e0b; }

        /* Code Block Styles - Scaled Down */
        .code-block { background-color: #1e293b; color: #e2e8f0; border-radius: 8px; overflow: hidden; margin: 1.6rem 0; box-shadow: 0 4px 12px rgba(0,0,0,0.15); position: relative; border: 1px solid #334155;}
        .code-block pre { margin: 0; padding: 1.4rem 1.6rem; overflow-x: auto; scrollbar-width: thin; scrollbar-color: #475569 #1e293b; font-size: 0.85rem;} /* Reduced font size */
        .code-block pre::-webkit-scrollbar { height: 8px; }
        .code-block pre::-webkit-scrollbar-track { background: #1e293b; border-radius: 0 0 6px 6px;}
        .code-block pre::-webkit-scrollbar-thumb { background-color: #475569; border-radius: 4px; border: 1.5px solid #1e293b;}
        .code-block pre::-webkit-scrollbar-thumb:hover { background-color: #64748b; }
        .code-block code { color: inherit; font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace; font-size: inherit; line-height: 1.7; background: none; padding: 0; white-space: pre; }

        /* Inline Code Styles - Scaled Down */
        code { background-color: #f1f5f9; border: 1px solid #e2e8f0; padding: 0.15rem 0.35rem; border-radius: 4px; font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace; font-size: 0.85em; color: #9333ea; word-wrap: break-word; }
        .code-block code { background-color: transparent; padding: 0; border-radius: 0; font-size: inherit; color: inherit; word-wrap: normal; white-space: pre; border: none; }

        /* Example Text Styles - Scaled Down */
        em { color: #64748b; font-style: italic; font-size: 0.9em; }
        ul em, ol em { display: block; margin-top: -0.6rem; margin-bottom: 0.8rem; }

        /* Hover Tooltip Style - Scaled Down */
        [title] { cursor: help; }
        /* Tooltip styling can be complex with pure CSS for mobile; consider a library if needed, but this is basic */
        [title]:hover { position: relative; }
        /* Simple tooltip for desktop, might be harder to trigger/see on mobile */
        @media (hover: hover) {
          [title]:hover::after {
              content: attr(title);
              position: absolute;
              bottom: 115%;
              left: 50%;
              transform: translateX(-50%);
              white-space: nowrap;
              background-color: #334155;
              color: #fff;
              padding: 0.3rem 0.6rem;
              border-radius: 4px;
              font-size: 0.75rem; /* Smaller tooltip font */
              z-index: 100;
              box-shadow: 0 1px 4px rgba(0,0,0,0.2);
          }
        }


        /* Specific Styles for Choosing Model Page - Scaled Down */
        .model-profile { margin-bottom: 1.8rem; }
        .model-title { display: flex; align-items: center; gap: 0.6rem; font-size: 1.4rem; margin-bottom: 0.8rem; border-bottom-width: 1.5px; padding-bottom: 0.4rem; border-bottom-style: solid; }
        .v4o-profile .model-title { border-color: #a5b4fc; color: #4f46e5;}
        .v5-profile .model-title { border-color: #fcd34d; color: #b45309;}
        .model-icon { font-size: 1.5em; line-height: 1; }
        .model-summary { font-size: 0.9rem; margin-bottom: 1.1rem; }
        .model-profile h4 { font-size: 1.05rem; margin-top: 1.3rem; margin-bottom: 0.6rem; }
        .model-characteristics { list-style: none; padding-left: 0; }
        .model-characteristics li { margin-bottom: 0.4rem; padding-left: 1.3em; position: relative; } /* Ensure relative positioning */
        .model-characteristics li::before { content: '✓'; position: absolute; left: 0; top: 0.15em; font-weight: bold; font-size: 0.9em;}
        .model-characteristics ul { margin-top: 0.4rem; padding-left: 1.2rem; }
         .model-characteristics ul li::before { content: '›'; color: #6b7280; font-size: 1em;}

        .comparison-table { display: block; /* Make table scrollable */ width: 100%; overflow-x: auto; border-collapse: collapse; margin-top: 1.5rem; font-size: 0.85rem; white-space: nowrap; /* Prevent wrapping */}
        .comparison-table th, .comparison-table td { border: 1px solid #e5e7eb; padding: 0.6rem 0.8rem; text-align: left; vertical-align: top; }
        .comparison-table th { background-color: #f9fafb; font-weight: 600; color: #374151; position: sticky; top: 0; /* Keep headers visible when scrolling */ }
        .comparison-table td.preferred { background-color: #e0f2fe; font-weight: 500; color: #0369a1;}
        .comparison-table td.preferred::before { content: '★ '; color: #0ea5e9;}

        /* Styles for Deployment Options - Scaled Down */
        .deployment-option { background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 1.3rem; margin-bottom: 1.3rem; transition: box-shadow 0.2s ease, border-color 0.2s ease;}
        .deployment-option:hover { box-shadow: 0 3px 8px rgba(0,0,0,0.05); border-color: #d1d5db;}
        .deployment-option h4 { margin-top: 0; margin-bottom: 0.6rem; font-size: 1.1rem; color: #1f2937;}
        .deployment-option ul { margin-top: 0.6rem; margin-bottom: 0.4rem;}

        /* Styles for Simulation Setup Box - Scaled Down */
        .simulation-setup-box { background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 1.5rem; margin: 1.5rem 0; }
        .sim-param { margin-bottom: 1.1rem; }
        .sim-param label { font-weight: 600; display: block; margin-bottom: 0.4rem; color: #334155; font-size: 0.9rem;}
        .sim-param input[type="text"], .sim-param input[type="number"], .sim-param select { width: 100%; padding: 0.5rem 0.7rem; border-radius: 5px; border: 1px solid #cbd5e1; font-size: 0.9rem; background-color: #fff; color: #475569;}
        .sim-param input[type="number"] { width: 80px; }
        .sim-param span { color: #64748b; font-size: 0.8rem; display: block; margin-top: 0.2rem;} /* Adjusted small text */
        .sim-run-button { padding: 0.6rem 1.1rem; font-weight: 600; cursor: not-allowed; background-color: #e2e8f0; color: #94a3b8; border: none; border-radius: 5px; font-size: 0.9rem; transition: background-color 0.2s ease;}

         /* Styles for Workflow Diagrams - Scaled Down */
        .workflow-example { margin-bottom: 2.5rem; }
        .workflow-diagram { margin-top: 1.5rem; padding: 1.1rem; background-color: #f9fafb; border: 1px dashed #d1d5db; border-radius: 6px;}
        .swarm-flowchart { margin-top: 1.5rem; padding: 1.1rem; background-color: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 6px;}
        .flowchart-branching { display: flex; justify-content: space-around; align-items: flex-start; margin-top: 0.7rem; gap: 0.5rem; }
        .flowchart-path { text-align: center; flex: 1; min-width: 120px; /* Prevent excessive shrinking */ }
        .flowchart-branch-label { color: #4b5563; display: block; margin-bottom: 0.3rem; font-weight: 500; font-size: 0.8em; }
        .workflow-label { text-align:center; margin: 0.3rem 0; color:#4b5563; font-style:italic; font-size: 0.8em; }
        .workflow-merge { display: flex; justify-content: center; align-items: flex-start; margin-top: 0.7rem; }
        .workflow-loop-arrow { display: block; margin: -8px auto; }
        .workflow-loop-arrow.down { transform: rotate(90deg); }
        .workflow-loop-arrow.up { transform: rotate(-90deg); }
        .workflow-loop-label { text-align:center; font-size:0.8em; margin-top:0.5rem; color: #6b7280; }

        .benefits-list li { padding-left: 1.5em; position: relative; } /* Ensure relative positioning */
        .benefits-list li::before { content: ''; width: 0.6em; height: 0.6em; border-radius: 50%; background-color: #818cf8; position: absolute; left: 0; top: 0.45em; }
        .benefits-list .benefit-icon { margin-right: 0.4rem; font-size: 1.1em; color: #6366f1; }

         /* Search Results Styles */
        .search-results-content { padding-top: 1.5rem; } /* Add some top padding for search results */
        .search-results-list { list-style: none; padding-left: 0; margin-top: 1.5rem; }
        .search-result-item {
            padding: 0.8rem 1rem;
            margin-bottom: 0.6rem;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            cursor: pointer;
            transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.1s ease;
        }
        .search-result-item:hover {
            background-color: #f4f6ff;
            border-color: #c7d2fe;
            transform: translateY(-1px);
        }
        .search-result-title {
            font-weight: 600;
            color: #4f46e5;
            font-size: 1rem; /* Keep titles readable */
            display: block;
        }
        .clear-search-button {
            margin-top: 1.5rem;
            padding: 0.5rem 1rem;
            font-weight: 500;
            cursor: pointer;
            background-color: #eef2ff;
            color: #4f46e5;
            border: 1px solid #c7d2fe;
            border-radius: 5px;
            font-size: 0.85rem;
            transition: background-color 0.2s ease, color 0.2s ease;
        }
        .clear-search-button:hover {
            background-color: #c7d2fe;
            color: #3730a3;
        }


        /* --- MOBILE OVERRIDES --- */
        @media (max-width: 768px) {
            :root {
              font-size: 13.5px; /* Slightly smaller base */
              --header-height: 52px;
            }

            .header {
              padding: 0 0.8rem; /* Reduced header padding */
              height: var(--header-height);
              justify-content: space-between; /* Adjust alignment */
            }

            .hamburger-btn {
              display: block; /* Show hamburger */
            }

            .logo { margin-right: 0.5rem; /* Reduce logo margin */ }
            .logo h1 { font-size: 1.1rem; }

            .nav-tabs {
               /* Allow tabs to take space, but hide on smallest screens if needed */
               flex-grow: 1;
               margin-left: 0.5rem;
               margin-right: 0.5rem;
               justify-content: flex-start; /* Align tabs left */
               -webkit-overflow-scrolling: touch; /* Smoother scrolling on iOS */
            }
             .tab { padding: 0.4rem 0.6rem; font-size: 0.85em; }

            .search-box {
                margin-left: 0; /* Remove fixed margin */
                flex-shrink: 1; /* Allow search to shrink */
                max-width: 160px; /* Limit search width */
            }
            .search-box input {
                width: 100%; /* Take available space */
                min-width: 80px; /* Minimum input size */
                font-size: 0.85em;
            }

            .sidebar {
                position: fixed;
                left: 0;
                top: 0; /* Align to top */
                height: 100vh; /* Full viewport height */
                transform: translateX(-100%); /* Start fully off-screen */
                z-index: 100; /* Above overlay */
                box-shadow: 2px 0 10px rgba(0,0,0,0.1);
                padding-top: calc(var(--header-height) + 1rem); /* Space below header */
                width: var(--sidebar-width-mobile);
                min-width: var(--sidebar-width-mobile);
                border-right: 1px solid #e5e7eb; /* Ensure border is visible */
            }
            .sidebar.open {
                transform: translateX(0); /* Slide in */
            }

            .main-content {
                height: calc(100vh - var(--header-height));
                width: 100%; /* Take full width */
                margin-left: 0; /* Remove margin when sidebar is hidden */
                transition: filter 0.3s ease-in-out;
            }
             /* Dim content when sidebar is open */
            .docs-container.sidebar-open .main-content {
                filter: blur(1px) brightness(0.9);
                overflow: hidden; /* Prevent scrolling of main content when sidebar is open */
            }


            .content {
                padding: 1.8rem 1.2rem; /* Reduced padding */
                margin: 1.2rem; /* Use margin instead of auto for spacing */
                max-width: none; /* Allow full width */
            }
            .content h2 { font-size: 1.5rem; margin-bottom: 1.5rem; padding-bottom: 0.8rem;}
            .content h3 { font-size: 1.25rem; margin-top: 2rem; margin-bottom: 1rem;}
            .content h4 { font-size: 1.05rem; margin-top: 1.5rem; margin-bottom: 0.8rem;}
            .content p, .content li { font-size: 0.88rem; } /* Slightly adjust base text */
            .content ul, .content ol { padding-left: 1.5rem; } /* Reduce list indent */

            .comparison-table { font-size: 0.8rem;} /* Further reduce table font */
            .comparison-table th, .comparison-table td { padding: 0.4rem 0.6rem; }

            .code-block pre { padding: 1rem 1.2rem; font-size: 0.8rem; }

             /* Make flowcharts stack vertically */
            .flowchart-branching {
                 flex-direction: column;
                 align-items: center; /* Center items vertically */
                 gap: 0.8rem; /* Adjust gap for vertical stack */
             }
            .flowchart-branching.parallel {
                /* Keep parallel layout if possible, but allow stacking */
                flex-direction: row; /* Revert for parallel */
                flex-wrap: wrap; /* Allow wrapping if needed */
                justify-content: center; /* Center wrapped items */
            }
            .flowchart-path { width: 90%; max-width: 250px; /* Limit width in vertical stack */ flex: none; /* Override flex: 1 */ }
            .flow-box { /* Ensure flow boxes are not too wide */
                max-width: 200px;
                padding: 0.6rem;
            }
            .flow-box p { font-size: 0.78em; }
        }

        @media (max-width: 480px) {
             /* Further adjustments for very small screens */
             :root { font-size: 13px; }
             .header { padding: 0 0.5rem; }
             .logo h1 { font-size: 1rem; }
             .nav-tabs { display: none; /* Hide tabs on very small screens? Or keep scrolling */ }
             .search-box { max-width: 120px; }
             .search-box input { font-size: 0.8em; }
             .content { padding: 1.5rem 1rem; margin: 0.8rem; }
             .content h2 { font-size: 1.3rem; }
             .content h3 { font-size: 1.15rem; }
        }

      `}</style>
    </div>
  );
}